import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import Messaging, { OFFER_PREFIX, postConversationMessage, countUnreadConversations } from '../../components/Messaging';
import { useSupportInbox, HelpSidebarButton, HelpSettingsCard } from '../../components/SupportInbox';
import NotificationBell from '../../components/NotificationBell';
import LanguageToggle from '../../components/LanguageToggle';
import { useLanguagePreference } from '../../i18n/useLanguagePreference';
import { startConversation } from '../../utils/startConversation';
import { sendNotificationEmail } from '../../utils/sendNotificationEmail';
import DeleteAccountModal from '../../components/DeleteAccountModal';
import ImageCropModal from '../../components/ImageCropModal';
import ProfileCompletionBanner from '../../components/ProfileCompletionBanner';
import { BENEFIT_OPTIONS } from '../../constants/benefitOptions';
import { renderJobDescription } from '../../utils/renderDescription';
import { parseEmployerNote, serializeEmployerNote, tagSentiment, noteTagLabelKey } from '../../utils/noteTags';
import { getInterviewCountdown, URGENCY_COLORS } from '../../utils/interviewCountdown';
import StatCard from '../../components/StatCard';
import LegalLinksRow from '../../components/LegalLinksRow';
import { joinParts } from '../../utils/joinParts';
import { countThisWeek } from '../../utils/activityFeed';
import { resolveEmployerId } from '../../utils/resolveEmployerId';
import { resolveUnionId } from '../../utils/resolveUnionId';
import PortfolioManager from '../../components/PortfolioManager';
import ZoomableImg from '../../components/ZoomableImg';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import EmployerCandidateCompare from '../../components/compare/EmployerCandidateCompare';
import { CompareToggle, CompareEntryButton } from '../../components/compare/CompareOverlay';
import { useCompareSelection } from '../../utils/useCompareSelection';
import { usePressFeedback } from '../../utils/usePressFeedback';
import LineIcon from '../../components/LineIcon';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GREEN     = '#1D9E75';
// Needs-attention icons (warnings, urgency). Status icons never use amber.
const YELLOW    = '#FACC15';
const ATTENTION_RED      = '#E24B4A';
const ATTENTION_RED_TEXT = '#F09595';
const TREND_GREEN        = '#5DCAA5';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const NAV_H     = 56;
const SIDEBAR_W = 190;

// Maps a job_offers.status value to the candidate-badge status it should display as, for the
// case where applications.status is still 'offer_sent' (i.e. no response has flipped it to
// something else yet). 'declined' isn't here -- it's handled separately in renderCandidateList since
// a decline flips applications.status away from 'offer_sent', so it needs to win regardless of
// the current applications.status rather than only within this bucket.
const OFFER_SENT_SUBSTATUS = { pending: 'offer_sent', accepted: 'offer_accepted' };

function IconGrid({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}
function IconBriefcase({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  );
}
function IconUsers({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconMsg({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function IconUserPlus({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="17" y1="11" x2="23" y2="11" />
    </svg>
  );
}
function IconBuilding({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
function IconSettings({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
function IconBolt({ color }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}
function IconDollar({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}
function IconCalendar({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}
function IconCheck({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="8 12 11 15 16 9" />
    </svg>
  );
}

function CompanyAvatar({ logoUrl, initials, size = 36, bg = NAVY_MID, textColor = WHITE }) {
  if (logoUrl) {
    return (
      <div style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: bg }}>
        <ZoomableImg src={logoUrl} alt="Company logo" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      backgroundColor: bg, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ fontWeight: 700, fontSize: size * 0.33, color: textColor, fontFamily: FONT_BODY }}>
        {initials}
      </span>
    </div>
  );
}

function CandidateNotes({ initialNote, applicationId, myUserId, myDisplayName }) {
  const { t, i18n } = useTranslation();
  const parsed = parseEmployerNote(initialNote);
  const [tags]             = useState(parsed.tags);
  const [text, setText]    = useState(parsed.text);
  const [saved, setSaved]  = useState(false);
  const [authorName, setAuthorName] = useState(parsed.authorName);

  const handleSave = async () => {
    const combined = serializeEmployerNote({ tags, origin: parsed.origin, addedAt: parsed.addedAt, text, authorId: myUserId, authorName: myDisplayName });
    await supabase.from('applications').update({ employer_notes: combined }).eq('id', applicationId);
    setAuthorName(myDisplayName);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      {parsed.origin === 'interview' && parsed.addedAt && (
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px', fontStyle: 'italic' }}>
          {t('employer.dashboard.candidates.addedDuringInterview', { date: new Date(parsed.addedAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }) })}
        </div>
      )}
      {authorName && (
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
          {t('employer.dashboard.candidates.noteLastEditedBy', { name: authorName })}
        </div>
      )}
      {tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' }}>
          {tags.map(tag => {
            const sentiment = tagSentiment(tag);
            const color = sentiment === 'negative' ? ATTENTION_RED : sentiment === 'positive' ? GREEN : AMBER;
            // Notes stored before the QUICK_NOTE_TAGS value/label split (or any tag that
            // doesn't match a known quick-tag) have no labelKey -- fall back to the raw
            // stored string rather than showing nothing.
            const labelKey = noteTagLabelKey(tag);
            return (
              <span
                key={tag}
                style={{
                  background: `${color}22`, color, border: `1px solid ${color}55`,
                  borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 9px',
                }}
              >
                {labelKey ? t(labelKey) : tag}
              </span>
            );
          })}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={t('employer.dashboard.candidates.notesPlaceholder')}
          rows={2}
          style={{
            flex: 1, padding: '7px 10px', fontSize: '12px',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '6px', color: WHITE, fontFamily: FONT_BODY,
            resize: 'vertical', outline: 'none', lineHeight: 1.5,
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
          <button
            onClick={handleSave}
            style={{
              background: NAVY, border: '1px solid rgba(255,255,255,0.22)', color: WHITE,
              fontSize: '11px', fontWeight: 600, padding: '6px 12px',
              borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, whiteSpace: 'nowrap',
            }}
          >{t('employer.dashboard.candidates.saveNoteButton')}</button>
          {saved && (
            <span style={{ fontSize: '11px', color: GREEN, fontWeight: 600 }}>{t('employer.dashboard.candidates.savedLabel')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTime12h(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h < 12 ? 'AM' : 'PM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

const WORK_TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 20; h++) {
    const minutes = h === 20 ? [0] : [0, 15, 30, 45];
    for (const m of minutes) {
      const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      opts.push({ value, label: formatTime12h(value) });
    }
  }
  return opts;
})();

function formatWorkHoursDisplay({ workStartTime, workEndTime, workDays }) {
  const parts = [];
  if (workStartTime || workEndTime) {
    const s = workStartTime ? formatTime12h(workStartTime) : '';
    const e = workEndTime ? formatTime12h(workEndTime) : '';
    if (s || e) parts.push([s, e].filter(Boolean).join(' - '));
  }
  const ALL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const DAY_LABELS = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };
  if (workDays && workDays.length > 0) {
    const sorted = [...workDays].sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b));
    const indices = sorted.map(d => ALL_DAYS.indexOf(d));
    const isConsecutive = indices.every((idx, i) => i === 0 || idx === indices[i - 1] + 1);
    if (sorted.length === 1) parts.push(DAY_LABELS[sorted[0]]);
    else if (isConsecutive) parts.push(`${DAY_LABELS[sorted[0]]} - ${DAY_LABELS[sorted[sorted.length - 1]]}`);
    else parts.push(sorted.map(d => DAY_LABELS[d]).join(', '));
  }
  return parts.join(', ');
}

function parseOfferedPay(str) {
  if (!str) return { payType: 'Hourly', payAmount: '' };
  const amount = str.replace(/[^0-9.]/g, '');
  if (str.includes('/year')) return { payType: 'Salary', payAmount: amount };
  if (str.includes('/project')) return { payType: 'Contract', payAmount: amount };
  return { payType: 'Hourly', payAmount: amount };
}

function time12hTo24h(str) {
  const m = str.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return '';
  let h = parseInt(m[1]);
  const min = m[2];
  const period = m[3].toUpperCase();
  if (period === 'AM' && h === 12) h = 0;
  if (period === 'PM' && h !== 12) h += 12;
  return `${String(h).padStart(2, '0')}:${min}`;
}

function parseWorkHoursToFields(str) {
  if (!str) return { workStartTime: '07:00', workEndTime: '15:30', workDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] };
  const timeMatch = str.match(/(\d{1,2}:\d{2}\s*(?:AM|PM))\s*[-–]\s*(\d{1,2}:\d{2}\s*(?:AM|PM))/i);
  const workStartTime = timeMatch ? (time12hTo24h(timeMatch[1].trim()) || '07:00') : '07:00';
  const workEndTime   = timeMatch ? (time12hTo24h(timeMatch[2].trim()) || '15:30') : '15:30';
  const ALL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const DAY_MAP = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };
  const rangeMatch = str.match(/(\w+day)\s*[-–]\s*(\w+day)/i);
  let workDays = [];
  if (rangeMatch) {
    const s = DAY_MAP[rangeMatch[1]], e = DAY_MAP[rangeMatch[2]];
    if (s && e) {
      const si = ALL_DAYS.indexOf(s), ei = ALL_DAYS.indexOf(e);
      if (si !== -1 && ei !== -1 && si <= ei) workDays = ALL_DAYS.slice(si, ei + 1);
    }
  }
  if (workDays.length === 0) {
    for (const [name, abbr] of Object.entries(DAY_MAP)) {
      if (str.includes(name)) workDays.push(abbr);
    }
  }
  return { workStartTime, workEndTime, workDays: workDays.length > 0 ? workDays : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] };
}

function parseCertsRequired(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; }
  catch { return typeof val === 'string' ? val.split(',').map(s => s.trim()).filter(Boolean) : []; }
}

const TAB_LABEL_KEYS = {
  'Dashboard': 'nav.employer.dashboard',
  'Candidates': 'nav.employer.candidates',
  'Messages': 'nav.employer.messages',
  'Company Profile': 'nav.employer.company',
  'Team': 'nav.employer.team',
  'Settings': 'nav.employer.settings',
};

const BENEFIT_KEY_MAP = {
  'Health insurance':      'healthInsurance',
  'Dental insurance':      'dentalInsurance',
  'Vision insurance':      'visionInsurance',
  '401(k)':                'retirement401k',
  'Paid time off':         'paidTimeOff',
  'Life insurance':        'lifeInsurance',
  'Tuition reimbursement': 'tuitionReimbursement',
  'Tool allowance':        'toolAllowance',
  'Overtime available':    'overtimeAvailable',
};

const DAY_ABBREV_KEYS = {
  Mon: 'mon', Tue: 'tue', Wed: 'wed', Thu: 'thu', Fri: 'fri', Sat: 'sat', Sun: 'sun',
};

const JOB_FILTER_LABEL_KEYS = {
  active: 'employer.dashboard.shared.filterActive',
  archived: 'employer.dashboard.shared.filterArchived',
  filled: 'employer.dashboard.shared.filterFilled',
};

const inp = {
  width: '100%', padding: '9px 12px', fontSize: '0.88rem',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
  fontFamily: FONT_BODY,
};
const sel = { ...inp, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' };
const fld = { marginBottom: '1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' };

const TABS = ['Dashboard', 'Candidates', 'Messages', 'Company Profile', 'Team', 'Settings'];

export default function EmployerDashboard() {
  usePressFeedback();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const tabLabel = (tab) => TAB_LABEL_KEYS[tab] ? t(TAB_LABEL_KEYS[tab]) : tab;

  // Existing state
  const [refreshKey, setRefreshKey]                 = useState(0);
  const [employer, setEmployer]                     = useState(null);
  useLanguagePreference(employer?.id);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

  // ── Acting-individual identity (distinct from `employer.id`, the shared company
  // identity every staff seat resolves to) -- this is the real logged-in person's own
  // auth.uid(), used for per-person attribution: messages, interviews, offers, notes. ──
  const [myUserId, setMyUserId]                     = useState(null);
  const [myStaffProfile, setMyStaffProfile]         = useState(null); // { display_name, avatar_url, role, is_active }
  const [profileNameForm, setProfileNameForm]       = useState('');
  const [profileSaving, setProfileSaving]           = useState(false);
  const [profileMsg, setProfileMsg]                 = useState('');
  const [profileMsgIsError, setProfileMsgIsError]   = useState(false);
  const [avatarCropFile, setAvatarCropFile]         = useState(null);
  const [avatarUploading, setAvatarUploading]       = useState(false);
  const [teamMembers, setTeamMembers]               = useState([]);
  const [staffActiveSaving, setStaffActiveSaving]   = useState({});
  const [staffActiveError, setStaffActiveError]     = useState('');
  // The name typed at signup (auth metadata full_name, also saved as employers.full_name for the
  // owner). The owner's employer_staff row is created without a display_name, so this is the
  // fallback everywhere a person is shown, before the company name or "Unnamed member".
  const [mySignupName, setMySignupName] = useState('');
  const myDisplayName = myStaffProfile?.display_name || mySignupName || employer?.company_name || '';
  const memberName = (m) => m?.display_name
    || (m?.user_id === myUserId ? mySignupName : '')
    || ((m?.role === 'owner' || m?.user_id === employer?.id) ? employer?.full_name : '')
    || t('employer.dashboard.team.unnamedMember');
  const [jobs, setJobs]                             = useState([]);
  const [jobDismissals, setJobDismissals]           = useState([]);
  const [expandedJob, setExpandedJob]               = useState(null);
  const [expandedFeedbackJob, setExpandedFeedbackJob] = useState(null);
  const [jobsArchiveFilter, setJobsArchiveFilter]   = useState('active');
  const [jobSearchQuery, setJobSearchQuery]         = useState('');
  const [jobSortBy, setJobSortBy]                   = useState('newest');
  const [candidates, setCandidates]                 = useState([]);
  const [candidatesError, setCandidatesError]       = useState(false);
  const [candidatesArchiveFilter, setCandidatesArchiveFilter] = useState('active');
  const hasAutoArchivedRef                          = useRef(false);
  const [loading, setLoading]                       = useState(true);
  const [activeTab, setActiveTab]                   = useState('Dashboard');
  const [openConversationId, setOpenConversationId] = useState(null);
  const [convUnreadCount, setUnreadCount]           = useState(0);
  const [mobileConvBadge, setMobileMessageBadge]    = useState(0);

  // Vocation Works Support conversation (see useSupportInbox). An unread support thread counts as
  // one unread conversation in the Messages badge, the same unit as every other conversation.
  const support = useSupportInbox({ supabase, accountType: 'employer', accountId: employer?.id, viewerId: myUserId });
  const supportUnread = support.hasUnread ? 1 : 0;
  const unreadCount = convUnreadCount + supportUnread;
  const mobileMessageBadge = mobileConvBadge + supportUnread;
  // Phone-width chat thread open: the Messages tab drops the support row and footer links.
  const [chatThreadOpen, setChatThreadOpen] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [upcomingInterviewsCount, setUpcomingInterviewsCount] = useState(0);
  const [interviewsScheduledThisWeek, setInterviewsScheduledThisWeek] = useState(0);
  const [now, setNow]                               = useState(() => new Date());

  // Company Profile tab
  const [companyForm, setCompanyForm]       = useState(null);
  const [companyFormMsg, setCompanyFormMsg] = useState('');
  const [companyFormMsgIsError, setCompanyFormMsgIsError] = useState(false);
  const logoInputRef                        = useRef(null);
  const [logoUploading, setLogoUploading]   = useState(false);
  const [logoMsg, setLogoMsg]               = useState('');
  const [logoMsgIsError, setLogoMsgIsError] = useState(false);
  const [cropFile, setCropFile]             = useState(null);

  // Settings tab
  const [pwForm, setPwForm] = useState({ newPw: '', confirmPw: '' });
  const [pwMsg, setPwMsg]   = useState('');
  const [pwMsgIsSuccess, setPwMsgIsSuccess] = useState(false);

  // Team tab
  const [staffInvites, setStaffInvites]       = useState([]);
  const [invitesLoaded, setInvitesLoaded]     = useState(false);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const [lastInviteLink, setLastInviteLink]   = useState('');
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [inviteErrorMsg, setInviteErrorMsg]   = useState('');
  const [inviteEmailInput, setInviteEmailInput] = useState('');
  const [inviteEmailStatusMsg, setInviteEmailStatusMsg] = useState(null); // null | { text, isError }
  const [planUsage, setPlanUsage] = useState(null); // null (not loaded yet) | { job_count, job_limit, user_count, user_limit }

  // Offer templates
  const [offerTemplates, setOfferTemplates]             = useState([]);
  const [showTemplateForm, setShowTemplateForm]         = useState(false);
  const [editingTemplate, setEditingTemplate]           = useState(null);
  const [templateSaving, setTemplateSaving]             = useState(false);
  const [deleteTemplateConfirm, setDeleteTemplateConfirm] = useState(null);
  const [templateMsg, setTemplateMsg]                   = useState('');
  // A template save/delete/load that didn't persist -- shown in red, never as a success.
  const [templateError, setTemplateError]               = useState('');
  const [pendingTemplateId, setPendingTemplateId]       = useState(null);
  const [loadTemplateId, setLoadTemplateId]             = useState('');
  const [templateForm, setTemplateForm] = useState({
    templateName: '', jobTitle: '', payType: 'Hourly', payAmount: '', paySchedule: 'Weekly',
    workLocation: 'On-site', workStartTime: '07:00', workEndTime: '15:30',
    workDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    overtimeAvailability: '', reportingManager: '',
    benefits: [], firstDayInstructions: '', personalMessage: '',
  });

  // Candidates filter
  const [candidateSearch, setCandidateSearch]             = useState('');
  const [candidateStatusFilter, setCandidateStatusFilter] = useState('');
  const [candidateTradeFilter, setCandidateTradeFilter]   = useState('');
  const [candidateShortlistOnly, setCandidateShortlistOnly] = useState(false);
  const [expandedCandidateJob, setExpandedCandidateJob]   = useState(null);
  const [expandedJobGroups, setExpandedJobGroups]         = useState({});
  // Candidates picked with ☆ for side-by-side comparison (application ids), and which job
  // posting's comparison is open -- a comparison is always within one posting's applicants.
  const candidateCompare = useCompareSelection();
  const [compareJobId, setCompareJobId]                   = useState(null);
  const [highlightWorkerId, setHighlightWorkerId]         = useState(null);

  // Reject candidate modal
  const [rejectModal, setRejectModal]         = useState(null);
  const [rejectFeedback, setRejectFeedback]   = useState('');
  const [rejectingInProgress, setRejectingInProgress] = useState(false);

  // Job offer modal
  const [offerModal, setOfferModal]       = useState(null);
  const [offerForm, setOfferForm]         = useState({
    jobTitle: '', payType: 'Hourly', payAmount: '', paySchedule: 'Weekly', startDate: '',
    workLocation: 'On-site', workStartTime: '07:00', workEndTime: '15:30',
    workDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    overtimeAvailability: '', reportingManager: '',
    benefits: [], message: '', firstDayInstructions: '',
  });
  const [offerSending, setOfferSending]   = useState(false);
  const [offerSentId, setOfferSentId]     = useState(null);
  const [attachedDocs, setAttachedDocs]   = useState([]);
  const [docsUploading, setDocsUploading] = useState(false);
  const docsInputRef                      = useRef(null);

  // Template document state
  const [templateDocs, setTemplateDocs]               = useState([]);      // new File objects in template form
  const [templateDocUrls, setTemplateDocUrls]         = useState([]);      // existing saved URLs in template form
  const [templateDocsUploading, setTemplateDocsUploading] = useState(false);
  const templateDocsInputRef                          = useRef(null);
  const [offerTemplateDocUrls, setOfferTemplateDocUrls] = useState([]);    // pre-loaded from template into offer modal
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [newCandidatesCount, setNewCandidatesCount] = useState(0);

  // ── Load data ─────────────────────────────────────────────
  useEffect(() => {
    const loadEmployerData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }

      const userId = session.user.id;
      setMyUserId(userId);

      // "Which employer does this login act as" is no longer always "this login's own id" --
      // an invited employer_staff seat has its own auth.uid() but acts on the owner's
      // employer_id.
      const employerId = await resolveEmployerId(supabase, userId);

      // This session isn't an employer account (e.g. a union account whose session
      // ended up here) -- send them to the dashboard that actually matches their role
      // instead of rendering employer-only chrome around empty/broken data.
      if (!employerId) {
        const unionId = await resolveUnionId(supabase, userId);
        navigate(unionId ? '/union/dashboard' : '/login');
        return;
      }

      const { data: employerRecord } = await supabase
        .from('employers')
        .select('*')
        .eq('id', employerId)
        .single();

      setEmployer(employerRecord);
      setEmailNotifications(employerRecord?.email_notifications || false);

      const { data: myStaffRow } = await supabase
        .from('employer_staff')
        .select('display_name, avatar_url, role, is_active')
        .eq('employer_id', employerId)
        .eq('user_id', userId)
        .maybeSingle();
      const signupName = (session.user.user_metadata?.full_name || (employerRecord?.id === userId ? employerRecord?.full_name : '') || '').trim();
      setMySignupName(signupName);
      // Carry the signup name into this person's own staff row once, so teammates and message
      // attribution see it too (same self-update Settings already does for display_name).
      if (myStaffRow && !myStaffRow.display_name && signupName) {
        const { error: nameErr } = await supabase.from('employer_staff').update({ display_name: signupName }).eq('employer_id', employerId).eq('user_id', userId);
        if (!nameErr) myStaffRow.display_name = signupName;
      }
      setMyStaffProfile(myStaffRow || null);
      setProfileNameForm(myStaffRow?.display_name || signupName);

      // Auto-archive candidates that have been in a final status for 30+ days.
      // Runs once per dashboard load, not on every refetch.
      if (!hasAutoArchivedRef.current) {
        hasAutoArchivedRef.current = true;
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
        await supabase
          .from('applications')
          .update({ archived_at: new Date().toISOString() })
          .eq('employer_id', employerId)
          .in('status', ['hired', 'rejected', 'not_selected', 'hire_fell_through'])
          .lt('created_at', thirtyDaysAgo)
          .is('archived_at', null);
      }

      const { data: jobsData } = await supabase
        .from('jobs')
        .select('*')
        .eq('employer_id', employerId)
        .in('status', ['active', 'filled'])
        .order('created_at', { ascending: false });

      setJobs(jobsData || []);

      const jobIdsForDismissals = (jobsData || []).map(j => j.id);
      const { data: dismissalsData } = jobIdsForDismissals.length > 0
        ? await supabase.from('job_dismissals').select('job_id, reason, reason_detail').in('job_id', jobIdsForDismissals)
        : { data: [] };
      setJobDismissals(dismissalsData || []);

      const { data: applicationsData, error: appsError } = await supabase
        .from('applications')
        .select('*')
        .eq('employer_id', employerId)
        .order('created_at', { ascending: false });

      if (appsError) {
        console.error('Failed to load applications:', appsError);
        setCandidatesError(true);
        setCandidates([]);
      } else if (applicationsData && applicationsData.length > 0) {
        setCandidatesError(false);
        const enriched = await Promise.all(
          applicationsData.map(async (app) => {
            const [{ data: workerData }, { data: jobData }, { data: workerProfileData }, { data: workerCertsData }, { data: latestOffer }] = await Promise.all([
              supabase.from('workers').select('id, first_name, last_name').eq('id', app.worker_id).single(),
              supabase.from('jobs').select('id, job_title, trade_category, job_description, job_type, city, state, salary_min, salary_max, certifications_required, certifications_preferred, benefits').eq('id', app.job_id).single(),
              supabase.from('worker_profiles').select('profile_photo_url').eq('id', app.worker_id).single(),
              supabase.from('worker_certifications').select('certification_name, verification_status').eq('worker_id', app.worker_id),
              supabase.from('job_offers').select('status').eq('application_id', app.id).order('created_at', { ascending: false }).limit(1).maybeSingle(),
            ]);

            return {
              ...app,
              application_id: app.id,
              worker: workerData,
              job: jobData,
              profile_photo_url: workerProfileData?.profile_photo_url || null,
              worker_cert_names: (workerCertsData || []).map(c => (c.certification_name || '').toLowerCase().trim()).filter(Boolean),
              has_verified_cert: (workerCertsData || []).some(c => c.verification_status === 'verified'),
              offer_response: latestOffer?.status || null,
            };
          })
        );
        setCandidates(enriched);
        // Candidates badge: count 'interested' apps since last Candidates tab visit,
        // plus any application whose status has changed (offer accepted/declined,
        // hired, rejected, reviewing, etc.) since that visit.
        const lastCandidatesVisit = localStorage.getItem('lastCandidatesVisit') || '0';
        console.log('[Badge] lastCandidatesVisit:', lastCandidatesVisit, 'enriched sample:', enriched.map(c => ({ id: c.application_id, status: c.status, created_at: c.created_at, updated_at: c.updated_at })));
        const newCount = enriched.filter(c => {
          const isNewApplication = c.status === 'interested' && c.created_at > lastCandidatesVisit;
          const hasStatusChange = c.updated_at > lastCandidatesVisit && c.updated_at !== c.created_at;
          return isNewApplication || hasStatusChange;
        }).length;
        setNewCandidatesCount(newCount);
        console.log('[Badge] newCount calculated:', newCount);
      } else {
        setCandidatesError(false);
        setCandidates([]);
      }

      // Excludes conversations this employer has hidden -- those don't appear in the
      // Messages list, so unread messages inside them would otherwise be un-clearable.
      const { data: visibleConvs } = await supabase
        .from('conversations')
        .select('id')
        .eq('employer_id', employerId)
        .is('employer_hidden_at', null);
      if (visibleConvs && visibleConvs.length > 0) {
        // Badge unit: unread conversations, matching the Messages list (see countUnreadConversations).
        const unreadConvCount = await countUnreadConversations(supabase, visibleConvs.map(c => c.id), 'employer');
        setUnreadCount(unreadConvCount);
        setMobileMessageBadge(unreadConvCount);
      } else {
        setUnreadCount(0);
        setMobileMessageBadge(0);
      }

      setLoading(false);
    };

    loadEmployerData();
  }, [navigate, refreshKey]); // eslint-disable-line

  // ── Team tab: sent employer_staff invites + the current member roster for this employer ──
  useEffect(() => {
    if (!employer?.id) return;
    const loadInvites = async () => {
      const { data } = await supabase
        .from('employer_invites')
        .select('*')
        .eq('employer_id', employer.id)
        .order('created_at', { ascending: false });
      setStaffInvites(data || []);
      setInvitesLoaded(true);
    };
    loadInvites();

    // Every current member (owner included -- the owner never redeemed an invite, so
    // they'd otherwise be entirely absent from a Team view built only from invites) has
    // their own display_name/avatar_url row here, unlike employer_invites which only
    // knows about invited-and-joined staff.
    const loadTeamMembers = async () => {
      const { data } = await supabase
        .from('employer_staff')
        // is_active drives the Inactive treatment below -- deactivated staff stay listed
        // (greyed out, reactivatable) rather than disappearing, since deactivation is a
        // reversible soft state, not a delete.
        .select('user_id, role, display_name, avatar_url, created_at, is_active')
        .eq('employer_id', employer.id)
        .order('created_at', { ascending: true });
      setTeamMembers(data || []);
    };
    loadTeamMembers();

    // Real-time job/user counts against the plan's limit (base + purchased add-ons, capped) --
    // this is a friendly pre-check only. The actual block is server-side (employer_can_add_user
    // inside redeem_employer_staff_invite, employer_can_add_job in the jobs RLS policy), so a
    // stale or failed read here just means the button doesn't warn ahead of time, not a security gap.
    const loadPlanUsage = async () => {
      const { data } = await supabase.rpc('employer_plan_usage', { p_employer_id: employer.id });
      if (data) setPlanUsage(data);
    };
    loadPlanUsage();
  }, [employer?.id]);

  // Soft deactivation: flips employer_staff.is_active, never deletes the row, so the member's
  // past messages, notes and candidate attribution all stay intact and they can be switched
  // back on later. The real enforcement is in the DB -- guard_employer_staff_deactivation
  // rejects anyone who isn't the owner and anyone targeting their own row -- so this only has
  // to keep the UI honest and surface whatever the trigger says if it does reject.
  const handleToggleStaffActive = async (member) => {
    if (!employer?.id) return;
    const deactivating = member.is_active !== false;
    const who = memberName(member);
    const confirmed = window.confirm(t(
      deactivating ? 'employer.dashboard.team.deactivateConfirm' : 'employer.dashboard.team.reactivateConfirm',
      { name: who },
    ));
    if (!confirmed) return;

    setStaffActiveSaving(prev => ({ ...prev, [member.user_id]: true }));
    setStaffActiveError('');
    const { error } = await supabase
      .from('employer_staff')
      .update({ is_active: !deactivating })
      .eq('employer_id', employer.id)
      .eq('user_id', member.user_id);
    setStaffActiveSaving(prev => ({ ...prev, [member.user_id]: false }));

    if (error) {
      setStaffActiveError(error.message);
      return;
    }
    setTeamMembers(prev => prev.map(m => m.user_id === member.user_id ? { ...m, is_active: !deactivating } : m));
    // Seats are counted server-side over active staff only, so re-read rather than adjusting
    // a local number -- this is what the invite form gates on.
    const { data: usage } = await supabase.rpc('employer_plan_usage', { p_employer_id: employer.id });
    if (usage) setPlanUsage(usage);
  };

  const handleGenerateInvite = async () => {
    if (!employer?.id) return;
    const trimmedEmail = inviteEmailInput.trim();
    setGeneratingInvite(true);
    setInviteErrorMsg('');
    setInviteLinkCopied(false);
    setInviteEmailStatusMsg(null);
    const { data: { session } } = await supabase.auth.getSession();
    const { data: newInvite, error } = await supabase
      .from('employer_invites')
      .insert({ employer_id: employer.id, created_by: session.user.id, email: trimmedEmail || null })
      .select('*')
      .single();
    if (error) { setGeneratingInvite(false); setInviteErrorMsg(error.message); return; }
    setStaffInvites(prev => [newInvite, ...prev]);
    const link = `${window.location.origin}/employer/join?code=${newInvite.invite_code}`;
    setLastInviteLink(link);

    // Emailing the invite is best-effort on top of the DB row (same pattern as
    // OrganizationDashboard's handleCreateInvite) -- the invite/link is already usable via
    // copy-link regardless of whether Resend succeeds, so a send failure shouldn't look like
    // the whole action failed.
    if (trimmedEmail) {
      try {
        const res = await fetch('/api/notify-interest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'employer_staff_invite',
            recipient_email: trimmedEmail,
            company_name: employer?.company_name || 'your company',
            invite_link: link,
          }),
        });
        setInviteEmailStatusMsg(res.ok
          ? { text: t('employer.dashboard.team.inviteEmailSent', { email: trimmedEmail }), isError: false }
          : { text: t('employer.dashboard.team.inviteEmailFailed'), isError: true });
      } catch (_) {
        setInviteEmailStatusMsg({ text: t('employer.dashboard.team.inviteEmailFailed'), isError: true });
      }
      setInviteEmailInput('');
    }
    setGeneratingInvite(false);
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(lastInviteLink).then(() => {
      setInviteLinkCopied(true);
      setTimeout(() => setInviteLinkCopied(false), 2000);
    });
  };

  // ── Sidebar: up to 2 soonest upcoming interviews, read straight from the interviews table ──
  // Interviews live independently of conversation state, so hiding/deleting a conversation
  // no longer affects this. Depends on refreshKey so the realtime `interviews` subscription
  // below (new interview, or a status change like accept/decline) forces a recompute.
  // Not capped at the sidebar's 2 -- upcomingInterviewsCount (the Dashboard stat card) needs
  // the real total, so the query fetches everything and the sidebar list just slices it.
  useEffect(() => {
    if (!employer?.id) return;
    const loadUpcomingInterviews = async () => {
      const { data: rows } = await supabase
        .from('interviews')
        .select('id, conversation_id, worker_id, interview_at, meeting_type, meeting_link, status, created_at')
        .eq('employer_id', employer.id)
        .not('status', 'in', '(cancelled,completed)')
        .gte('interview_at', new Date().toISOString())
        .order('interview_at', { ascending: true });
      if (!rows?.length) { setUpcomingInterviews([]); setUpcomingInterviewsCount(0); setInterviewsScheduledThisWeek(0); return; }
      setUpcomingInterviewsCount(rows.length);
      setInterviewsScheduledThisWeek(countThisWeek(rows, 'created_at'));

      const mapped = await Promise.all(rows.slice(0, 2).map(async (upcoming) => {
        const { data: workerRow } = await supabase.from('workers').select('first_name, last_name').eq('id', upcoming.worker_id).single();
        const otherPartyName = workerRow ? `${workerRow.first_name || ''} ${workerRow.last_name || ''}`.trim() : '';
        return {
          id: upcoming.id,
          conversationId: upcoming.conversation_id,
          otherPartyName,
          interviewAt: upcoming.interview_at,
          meetingType: upcoming.meeting_type,
          meetingLink: upcoming.meeting_link,
          status: upcoming.status,
        };
      }));
      setUpcomingInterviews(mapped);
    };
    loadUpcomingInterviews();
  }, [employer?.id, refreshKey]); // eslint-disable-line

  // ── Live countdown labels: re-render the upcoming-interview card(s) every 30s so the
  // "Interview in X min" / "Starting now" labels stay accurate without a page reload ──
  useEffect(() => {
    if (!upcomingInterviews.length) return;
    const tick = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(tick);
  }, [upcomingInterviews.length]);

  // ── Clicking the upcoming-interview card joins the meeting directly when a link
  // exists; otherwise falls back to opening the associated conversation ──────────
  const openUpcomingInterview = (interview) => {
    if (interview.meetingLink) {
      if (interview.meetingType === 'daily') {
        window.open(`/interview?interviewId=${interview.id}`, '_blank');
      } else {
        window.open(interview.meetingLink, '_blank', 'noopener,noreferrer');
      }
    } else {
      setActiveTab('Messages');
      setOpenConversationId(interview.conversationId);
    }
  };

  // ── Handle navigation state from WorkerProfile ────────────
  useEffect(() => {
    if (location.state?.activeTab) setActiveTab(location.state.activeTab);
    else if (location.state?.tab) setActiveTab(location.state.tab);
    if (location.state?.conversationId) setOpenConversationId(location.state.conversationId);
  }, []); // eslint-disable-line

  // ── Initialize company profile form ───────────────────────
  useEffect(() => {
    if (!employer) return;
    setCompanyForm({
      companyName:        employer.company_name   || '',
      fullName:           employer.full_name      || '',
      jobTitle:           employer.job_title      || '',
      companySize:        employer.company_size   || '',
      city:               employer.city           || '',
      state:              employer.state          || '',
      about:              employer.about          || '',
      website:            employer.website        || '',
      yearFounded:        employer.year_founded   != null ? String(employer.year_founded) : '',
      industry:           employer.industry       || '',
      companyUnionStatus: employer.union_status   || '',
      typicalBenefits:    Array.isArray(employer.typical_benefits) ? employer.typical_benefits : [],
      otherBenefits:      employer.other_benefits || '',
    });
  }, [employer]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${NAVY}; font-family: 'DM Sans', sans-serif; }
      input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.28); }
      select option { background-color: ${NAVY_MID}; color: ${WHITE}; }
      @media (max-width: 767px) {
        .dash-sidebar { display: none !important; }
        .dash-main { margin-left: 0 !important; padding-left: 16px !important; padding-right: 16px !important; padding-bottom: calc(68px + env(safe-area-inset-bottom)) !important; overflow-x: hidden !important; }
        .dash-bottom-nav { display: flex !important; }
        .dash-nav-company { display: none !important; }
        .dash-tab-bar { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (activeTab === 'Messages') { setUnreadCount(0); setMobileMessageBadge(0); }
    if (activeTab === 'Candidates') {
      localStorage.setItem('lastCandidatesVisit', new Date().toISOString());
      setNewCandidatesCount(0);
    }
  }, [activeTab]);

  // Load offer templates whenever employer becomes available
  useEffect(() => {
    if (!employer?.id) return;
    supabase.from('offer_templates')
      .select('*')
      .eq('employer_id', employer.id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) { setTemplateError(t('employer.dashboard.settings.templateLoadFailed')); return; }
        setOfferTemplates(data || []);
      });
  }, [employer?.id]); // eslint-disable-line

  // ── Realtime subscriptions ────────────────────────────────
  useEffect(() => {
    if (!employer?.id) return;

    // A dropped realtime connection (network blip, backgrounded tab, mobile network
    // handoff) otherwise fails silently forever with no reconnect, leaving these two
    // channels stale until a manual reload. Resubscribe with a fresh channel name on
    // CHANNEL_ERROR/TIMED_OUT instead of leaving it dead.
    let cancelled = false;
    const subscribeWithRetry = (buildChannel, label) => {
      let current = null;
      const start = () => {
        current = buildChannel();
        current.subscribe((status) => {
          if (cancelled) return;
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.warn(`[realtime] ${label} channel ${status.toLowerCase()}, resubscribing…`);
            supabase.removeChannel(current);
            setTimeout(start, 2000);
          }
        });
      };
      start();
      return { remove: () => { if (current) supabase.removeChannel(current); } };
    };

    const appsChannel = subscribeWithRetry(() => supabase
      .channel(`applications:employer:${employer.id}:${Date.now()}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'applications', filter: `employer_id=eq.${employer.id}` },
        () => setRefreshKey(k => k + 1)
      )
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'applications', filter: `employer_id=eq.${employer.id}` },
        () => setRefreshKey(k => k + 1)
      ), 'applications');

    const offersChannel = subscribeWithRetry(() => supabase
      .channel(`job_offers:employer:${employer.id}:${Date.now()}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'job_offers', filter: `employer_id=eq.${employer.id}` },
        () => setRefreshKey(k => k + 1)
      ), 'job_offers');

    // Catches a conversation being hidden/unhidden, which affects the unread-message count.
    const convsChannel = supabase
      .channel(`conversations:employer:${employer.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations', filter: `employer_id=eq.${employer.id}` },
        () => setRefreshKey(k => k + 1)
      )
      .subscribe();

    // Interviews live outside conversation state entirely, so a new interview or a
    // status change (accept/decline) needs its own subscription to recompute the card.
    const interviewsChannel = supabase
      .channel(`interviews:employer:${employer.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'interviews', filter: `employer_id=eq.${employer.id}` },
        () => setRefreshKey(k => k + 1)
      )
      .subscribe();

    return () => {
      cancelled = true;
      appsChannel.remove();
      offersChannel.remove();
      supabase.removeChannel(convsChannel);
      supabase.removeChannel(interviewsChannel);
    };
  }, [employer?.id]); // eslint-disable-line

  // Scroll to and briefly highlight the candidate a notification deep-linked to.
  // Works off the raw `candidates` list rather than the later-derived candidateGroups/
  // filteredCandidates consts, since hooks must run unconditionally before the
  // `if (loading) return` below.
  useEffect(() => {
    if (!highlightWorkerId || activeTab !== 'Candidates') return;
    const target = candidates.find(c => c.worker_id === highlightWorkerId);
    if (!target) { setHighlightWorkerId(null); return; }

    if (candidateSearch) { setCandidateSearch(''); return; }
    if (candidateStatusFilter) { setCandidateStatusFilter(''); return; }
    if (candidateTradeFilter) { setCandidateTradeFilter(''); return; }
    if (candidateShortlistOnly) { setCandidateShortlistOnly(false); return; }
    const wantsArchived = !!target.archived_at;
    if (wantsArchived && candidatesArchiveFilter !== 'archived') { setCandidatesArchiveFilter('archived'); return; }
    if (!wantsArchived && candidatesArchiveFilter === 'archived') { setCandidatesArchiveFilter('active'); return; }

    const jobId = target.job_id || 'unknown';
    if (!expandedJobGroups[jobId]) { setExpandedJobGroups(prev => ({ ...prev, [jobId]: true })); return; }

    const id = highlightWorkerId;
    requestAnimationFrame(() => {
      document.querySelector(`[data-worker-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const t = setTimeout(() => setHighlightWorkerId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightWorkerId, activeTab, candidates, candidateSearch, candidateStatusFilter, candidateTradeFilter, candidateShortlistOnly, candidatesArchiveFilter, expandedJobGroups]); // eslint-disable-line

  // Owner = the employers row's own login, or an active owner seat -- the same rule
  // trg_protect_employer_deactivation and delete_own_employer_account() enforce.
  const isEmployerOwner = !!employer && !!myUserId && (
    employer.id === myUserId || (myStaffProfile?.role === 'owner' && myStaffProfile?.is_active !== false)
  );

  if (loading) return (
    <div style={{ background: '#0D1B2A', height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#F5A623', fontFamily: 'sans-serif', fontSize: '16px' }}>{t('employer.dashboard.shared.loadingDashboard')}</div>
    </div>
  );

  // ── Handlers ──────────────────────────────────────────────
  const handleMessageCandidate = async (candidate) => {
    const convId = await startConversation(supabase, candidate.worker_id, employer.id, candidate.job_id, 'employer');
    if (!convId) return;
    setOpenConversationId(convId);
    setActiveTab('Messages');
  };

  const handleToggleEmailNotifications = async () => {
    const newVal = !emailNotifications;
    setEmailNotifications(newVal);
    await supabase.from('employers').update({ email_notifications: newVal }).eq('id', employer.id);
  };

  const handleUpdateCandidateStatus = async (candidate, newStatus, feedback) => {
    const updatePayload = { status: newStatus };
    if (newStatus === 'rejected' && feedback && feedback.trim()) {
      updatePayload.rejection_feedback = feedback.trim();
    }
    const { error } = await supabase
      .from('applications')
      .update(updatePayload)
      .eq('id', candidate.application_id);
    if (!error) {
      setCandidates(candidates.map(c =>
        c.application_id === candidate.application_id
          ? { ...c, status: newStatus }
          : c
      ));
      const statusLabel = newStatus === 'reviewing' ? 'under review'
        : newStatus === 'hire_fell_through' ? 'marked as not hired — this position is open again'
        : 'not selected';
      supabase.from('notifications').insert({
        user_id:   candidate.worker_id,
        user_type: 'worker',
        type:      'status_change',
        message:   `Your application for ${candidate.job?.job_title} is now ${statusLabel}`,
        link_to:   JSON.stringify({ tab: 'Applications' }),
      }).then(() => {});
      sendNotificationEmail(supabase, {
        recipientId:   candidate.worker_id,
        recipientType: 'worker',
        type:          'status_change',
        job_title:     candidate.job?.job_title || '',
        status_label:  statusLabel,
      });
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectModal) return;
    setRejectingInProgress(true);
    await handleUpdateCandidateStatus(rejectModal, 'rejected', rejectFeedback);
    setRejectingInProgress(false);
    setRejectModal(null);
    setRejectFeedback('');
  };

  const handleArchiveCandidate = async (applicationId) => {
    const archivedAt = new Date().toISOString();
    await supabase.from('applications').update({ archived_at: archivedAt }).eq('id', applicationId);
    setCandidates(prev => prev.map(c => c.application_id === applicationId ? { ...c, archived_at: archivedAt } : c));
  };

  const handleUnarchiveCandidate = async (applicationId) => {
    await supabase.from('applications').update({ archived_at: null }).eq('id', applicationId);
    setCandidates(prev => prev.map(c => c.application_id === applicationId ? { ...c, archived_at: null } : c));
  };

  const OFFER_FORM_BLANK = {
    jobTitle: '', payType: 'Hourly', payAmount: '', paySchedule: 'Weekly', startDate: '',
    workLocation: 'On-site', workStartTime: '07:00', workEndTime: '15:30',
    workDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    overtimeAvailability: '', reportingManager: '',
    benefits: [], message: '', firstDayInstructions: '',
  };

  const TEMPLATE_FORM_BLANK = {
    templateName: '', jobTitle: '', payType: 'Hourly', payAmount: '', paySchedule: 'Weekly',
    workLocation: 'On-site', workStartTime: '07:00', workEndTime: '15:30',
    workDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    overtimeAvailability: '', reportingManager: '',
    benefits: [], firstDayInstructions: '', personalMessage: '',
  };

  // public.offer_templates column -> offer form field. Every column a template can carry
  // (template_name and attached_documents are handled separately).
  const TEMPLATE_OFFER_FIELDS = [
    ['job_title', 'jobTitle'], ['pay_type', 'payType'], ['pay_amount', 'payAmount'],
    ['pay_schedule', 'paySchedule'], ['work_location', 'workLocation'],
    ['work_start_time', 'workStartTime'], ['work_end_time', 'workEndTime'], ['work_days', 'workDays'],
    ['overtime_availability', 'overtimeAvailability'], ['reporting_manager', 'reportingManager'],
    ['benefits', 'benefits'], ['first_day_instructions', 'firstDayInstructions'],
    ['personal_message', 'message'],
  ];

  // Only the fields the template actually has a value for, so loading it fills those and
  // leaves everything else in the offer form (the job's own title and benefits, anything
  // already typed) alone instead of resetting it to a blank or a default.
  const templateToOfferFields = (tpl) => Object.fromEntries(
    TEMPLATE_OFFER_FIELDS
      .filter(([col]) => {
        const v = tpl[col];
        return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
      })
      .map(([col, key]) => [key, tpl[col]])
  );

  // Open offer modal, optionally pre-filling from a pending template
  const openOfferModal = (candidate) => {
    const template = pendingTemplateId ? offerTemplates.find(t => t.id === pendingTemplateId) : null;
    setOfferModal(candidate);
    setAttachedDocs([]);
    if (template) {
      setOfferForm({
        ...OFFER_FORM_BLANK,
        jobTitle: candidate.job?.job_title || '',
        benefits: Array.isArray(candidate.job?.benefits) ? candidate.job.benefits : [],
        ...templateToOfferFields(template),
        startDate: '',
      });
      setOfferTemplateDocUrls(template.attached_documents || []);
      setLoadTemplateId(pendingTemplateId);
      setPendingTemplateId(null);
    } else {
      setOfferForm({
        ...OFFER_FORM_BLANK,
        jobTitle: candidate.job?.job_title || '',
        benefits: Array.isArray(candidate.job?.benefits) ? candidate.job.benefits : [],
      });
      setOfferTemplateDocUrls([]);
      setLoadTemplateId('');
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.templateName.trim()) return;
    setTemplateSaving(true);
    setTemplateError('');
    setTemplateMsg('');

    // Upload any newly added template docs
    const allDocUrls = [...templateDocUrls];
    let docUploadFailed = false;
    if (templateDocs.length > 0) {
      setTemplateDocsUploading(true);
      const safeName = templateForm.templateName.trim().replace(/[^a-z0-9_-]/gi, '_') || 'template';
      for (const file of templateDocs) {
        const path = `${employer.id}/templates/${safeName}/${file.name}`;
        try {
          const { error: uploadErr } = await supabase.storage
            .from('offer-letters')
            .upload(path, file, { upsert: true, contentType: 'application/pdf' });
          if (!uploadErr) {
            const { data: urlData } = supabase.storage.from('offer-letters').getPublicUrl(path);
            allDocUrls.push(urlData.publicUrl);
          } else {
            docUploadFailed = true;
          }
        } catch (ex) {
          docUploadFailed = true;
        }
      }
      setTemplateDocsUploading(false);
    }
    // A PDF that didn't upload would otherwise just be missing from the saved template.
    if (docUploadFailed) {
      setTemplateError(t('employer.dashboard.settings.templateDocUploadFailed'));
      setTemplateSaving(false);
      return;
    }

    // employer.id is the staff-aware id from resolveEmployerId, so an invited staff seat saves
    // under the employer it acts for, which is what the is_employer_staff(employer_id) policy checks.
    const row = {
      employer_id:           employer.id,
      template_name:         templateForm.templateName.trim(),
      job_title:             templateForm.jobTitle,
      pay_type:              templateForm.payType,
      pay_amount:            templateForm.payAmount,
      pay_schedule:          templateForm.paySchedule,
      work_location:         templateForm.workLocation,
      work_start_time:       templateForm.workStartTime,
      work_end_time:         templateForm.workEndTime,
      work_days:             templateForm.workDays,
      overtime_availability: templateForm.overtimeAvailability,
      reporting_manager:     templateForm.reportingManager,
      benefits:              templateForm.benefits,
      first_day_instructions: templateForm.firstDayInstructions,
      personal_message:      templateForm.personalMessage,
      attached_documents:    allDocUrls.length > 0 ? allDocUrls : null,
    };
    // A write only counts when the row comes back: an RLS-filtered update or insert can
    // "succeed" with zero rows, which must never read as saved.
    const { data: savedRows, error } = editingTemplate
      ? await supabase.from('offer_templates').update(row).eq('id', editingTemplate).eq('employer_id', employer.id).select()
      : await supabase.from('offer_templates').insert(row).select();
    const saved = !error && savedRows?.length === 1 ? savedRows[0] : null;
    if (!saved) {
      setTemplateError(t('employer.dashboard.settings.templateSaveFailed'));
      setTemplateSaving(false);
      return;
    }
    setOfferTemplates(prev => editingTemplate ? prev.map(x => x.id === saved.id ? saved : x) : [saved, ...prev]);
    setShowTemplateForm(false);
    setEditingTemplate(null);
    setTemplateForm(TEMPLATE_FORM_BLANK);
    setTemplateDocs([]);
    setTemplateDocUrls([]);
    setTemplateMsg(t(editingTemplate ? 'employer.dashboard.settings.templateUpdatedMsg' : 'employer.dashboard.settings.templateSavedMsg'));
    setTimeout(() => setTemplateMsg(''), 3000);
    setTemplateSaving(false);
  };

  const handleDeleteTemplate = async (id) => {
    setTemplateError('');
    const { data: deleted, error } = await supabase.from('offer_templates').delete().eq('id', id).eq('employer_id', employer.id).select('id');
    if (error || deleted?.length !== 1) {
      setTemplateError(t('employer.dashboard.settings.templateDeleteFailed'));
      setDeleteTemplateConfirm(null);
      return;
    }
    setOfferTemplates(prev => prev.filter(x => x.id !== id));
    setDeleteTemplateConfirm(null);
  };

  const handleEditTemplate = (t) => {
    setTemplateForm({
      templateName:        t.template_name         || '',
      jobTitle:            t.job_title             || '',
      payType:             t.pay_type              || 'Hourly',
      payAmount:           t.pay_amount            || '',
      paySchedule:         t.pay_schedule          || 'Weekly',
      workLocation:        t.work_location         || 'On-site',
      workStartTime:       t.work_start_time       || '07:00',
      workEndTime:         t.work_end_time         || '15:30',
      workDays:            t.work_days             || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      overtimeAvailability: t.overtime_availability || '',
      reportingManager:    t.reporting_manager     || '',
      benefits:            t.benefits              || [],
      firstDayInstructions: t.first_day_instructions || '',
      personalMessage:     t.personal_message      || '',
    });
    setTemplateDocUrls(t.attached_documents || []);
    setTemplateDocs([]);
    setTemplateError('');
    setEditingTemplate(t.id);
    setShowTemplateForm(true);
  };

  const handleSendOfferFromMessaging = async ({ worker_id, job_id }) => {
    let candidate = candidates.find(c => c.worker_id === worker_id && c.job_id === job_id);
    if (!candidate) {
      let appQuery = supabase
        .from('applications')
        .select('*')
        .eq('worker_id', worker_id)
        .eq('employer_id', employer.id);
      if (job_id) appQuery = appQuery.eq('job_id', job_id);
      const { data: appData } = await appQuery.maybeSingle();
      if (appData) {
        const [{ data: workerData }, { data: jobData }] = await Promise.all([
          supabase.from('workers').select('id, first_name, last_name').eq('id', worker_id).single(),
          job_id
            ? supabase.from('jobs').select('id, job_title, trade_category, job_description, job_type, city, state, salary_min, salary_max, certifications_required, certifications_preferred, benefits').eq('id', job_id).single()
            : Promise.resolve({ data: null }),
        ]);
        candidate = { ...appData, application_id: appData.id, worker: workerData, job: jobData };
      }
    }
    if (candidate) {
      openOfferModal(candidate);
    }
  };

  const handleOpenUpdatedOffer = async (candidate) => {
    setOfferModal(candidate);
    setAttachedDocs([]);
    const { data: lastOffer } = await supabase
      .from('job_offers')
      .select('*')
      .eq('application_id', candidate.application_id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (lastOffer) {
      const { payType, payAmount } = parseOfferedPay(lastOffer.offered_pay);
      const { workStartTime, workEndTime, workDays } = parseWorkHoursToFields(lastOffer.work_hours);
      setOfferForm({
        jobTitle:            lastOffer.job_title             || candidate.job?.job_title || '',
        payType,
        payAmount,
        paySchedule:         lastOffer.pay_schedule          || 'Weekly',
        startDate:           lastOffer.start_date ? lastOffer.start_date.split('T')[0] : '',
        workLocation:        lastOffer.work_location         || 'On-site',
        workStartTime,
        workEndTime,
        workDays,
        overtimeAvailability: lastOffer.overtime_availability || '',
        reportingManager:    lastOffer.reporting_manager     || '',
        benefits:            Array.isArray(lastOffer.benefits) ? lastOffer.benefits : [],
        message:             lastOffer.message               || '',
        firstDayInstructions: lastOffer.first_day_instructions || '',
      });
    } else {
      setOfferForm({
        ...OFFER_FORM_BLANK,
        jobTitle: candidate.job?.job_title || '',
        benefits: Array.isArray(candidate.job?.benefits) ? candidate.job.benefits : [],
      });
    }
  };

  const handleSendOffer = async () => {
    if (!offerModal) return;
    console.log('[handleSendOffer] start', { applicationId: offerModal.application_id, docCount: attachedDocs.length });
    setOfferSending(true);
    let closeDeferred = false;

    try {
      // Upload all attached documents — failures are non-fatal per file
      const attachedDocUrls = [...offerTemplateDocUrls];
      if (attachedDocs.length > 0) {
        setDocsUploading(true);
        console.log('[handleSendOffer] uploading', attachedDocs.length, 'document(s)');
        for (const file of attachedDocs) {
          const path = `${employer.id}/${offerModal.application_id}/${file.name}`;
          try {
            const { error: uploadErr } = await supabase.storage
              .from('offer-letters')
              .upload(path, file, { upsert: true, contentType: 'application/pdf' });
            if (uploadErr) {
              console.log('[handleSendOffer] upload error for', file.name, '(non-fatal):', uploadErr.message);
            } else {
              const { data: urlData } = supabase.storage.from('offer-letters').getPublicUrl(path);
              attachedDocUrls.push(urlData.publicUrl);
              console.log('[handleSendOffer] uploaded', file.name, '->', urlData.publicUrl);
            }
          } catch (uploadEx) {
            console.log('[handleSendOffer] upload exception for', file.name, '(non-fatal):', uploadEx);
          }
        }
        setDocsUploading(false);
      }

      const paySuffix = offerForm.payType === 'Salary' ? '/year' : offerForm.payType === 'Contract' ? '/project' : '/hr';
      const computedPay = offerForm.payAmount ? `$${Number(offerForm.payAmount).toLocaleString()}${paySuffix}` : '';
      const computedWorkHours = formatWorkHoursDisplay(offerForm);

      console.log('[handleSendOffer] inserting into job_offers', { docUrls: attachedDocUrls });
      const { data: insertedOffer, error } = await supabase.from('job_offers').insert({
        application_id:        offerModal.application_id,
        worker_id:             offerModal.worker_id,
        employer_id:           employer.id,
        job_id:                offerModal.job_id,
        job_title:             offerForm.jobTitle,
        offered_pay:           computedPay,
        pay_schedule:          offerForm.paySchedule,
        start_date:            offerForm.startDate || null,
        work_location:         offerForm.workLocation,
        work_hours:            computedWorkHours,
        overtime_availability: offerForm.overtimeAvailability,
        reporting_manager:     offerForm.reportingManager,
        benefits:              offerForm.benefits,
        message:               offerForm.message,
        first_day_instructions: offerForm.firstDayInstructions || null,
        attached_documents:    attachedDocUrls.length > 0 ? attachedDocUrls : null,
        created_by:            myUserId,
      }).select('id').single();
      console.log('[handleSendOffer] job_offers insert result:', { error: error?.message ?? null });

      if (!error) {
        await supabase.from('applications').update({ status: 'offer_sent' }).eq('id', offerModal.application_id);
        setCandidates(prev => prev.map(c =>
          c.application_id === offerModal.application_id ? { ...c, status: 'offer_sent' } : c
        ));
        // Post the offer into the worker's Messages thread as an offer card (role, pay, start
        // date, "View offer"); the employer side sees it as "Offer sent". The notification then
        // links to that thread. If the thread can't be opened, it still links to Applications.
        let offerConvId = null;
        try {
          offerConvId = await startConversation(supabase, offerModal.worker_id, employer.id, offerModal.job_id, 'employer');
          if (offerConvId) {
            await postConversationMessage(supabase, {
              conversationId: offerConvId, senderId: myUserId || employer.id, senderType: 'employer', otherPartyId: offerModal.worker_id,
              content: OFFER_PREFIX + JSON.stringify({
                offer_id: insertedOffer?.id || null, application_id: offerModal.application_id,
                job_title: offerForm.jobTitle || offerModal.job?.job_title || '', pay: computedPay, start_date: offerForm.startDate || null,
              }),
            });
          }
        } catch (msgErr) {
          console.log('[handleSendOffer] offer message not posted (non-fatal):', msgErr);
        }
        supabase.from('notifications').insert({
          user_id:   offerModal.worker_id,
          user_type: 'worker',
          type:      'job_offer',
          message:   `${employer.company_name} sent you a job offer for ${offerModal.job?.job_title}`,
          link_to:   JSON.stringify(offerConvId
            ? { tab: 'Messages', conversation_id: offerConvId }
            : { tab: 'Applications', application_id: offerModal.application_id }),
        }).then(() => {});
        sendNotificationEmail(supabase, {
          recipientId:   offerModal.worker_id,
          recipientType: 'worker',
          type:          'job_offer',
          company_name:  employer.company_name,
          job_title:     offerModal.job?.job_title || '',
        });
        setOfferSentId(offerModal.application_id);
        console.log('[handleSendOffer] success — closing modal in 2s');
        closeDeferred = true;
        setTimeout(() => {
          setOfferModal(null);
          setOfferSentId(null);
          setOfferForm(OFFER_FORM_BLANK);
          setAttachedDocs([]);
        }, 2000);
      } else {
        console.log('[handleSendOffer] insert error:', error.message);
      }
    } catch (ex) {
      console.log('[handleSendOffer] unexpected exception:', ex);
    } finally {
      setOfferSending(false);
      if (!closeDeferred) {
        console.log('[handleSendOffer] closing modal in finally (error path)');
        setOfferModal(null);
        setOfferForm(OFFER_FORM_BLANK);
        setAttachedDocs([]);
      }
    }
  };

  const handleMarkFilled = async (jobId, e) => {
    e.stopPropagation();
    // Marking a job filled and archiving it are genuinely separate actions (see
    // handleArchiveJob/handleUnarchiveJob below -- archiving has its own confirm and cascades
    // to the job's applications, which filling never has). Setting archived_at here too used
    // to conflate the two: the "Archived" filter checks archived_at before status, so a
    // freshly-filled job always got stuck there instead of showing under "Filled". Filling a
    // job should never implicitly archive it -- only the real Archive action should.
    await supabase.from('jobs').update({ status: 'filled' }).eq('id', jobId);
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'filled' } : j));
  };

  const handleReopenJob = async (jobId, e) => {
    e.stopPropagation();
    // Also clears archived_at, in case this job was separately, explicitly archived at some
    // point while filled (handleArchiveJob) -- reopening should always make it visible again
    // under "Active", not leave it stuck under "Archived".
    await supabase.from('jobs').update({ status: 'active', archived_at: null }).eq('id', jobId);
    setJobs(jobs.map(j => j.id === jobId ? { ...j, status: 'active', archived_at: null } : j));
  };

  const handleDeleteJob = async (jobId, e) => {
    e.stopPropagation();
    if (!window.confirm(t('employer.dashboard.home.deleteJobConfirm'))) return;
    await supabase.from('jobs').delete().eq('id', jobId);
    setJobs(jobs.filter(j => j.id !== jobId));
  };

  const handleArchiveJob = async (jobId, e) => {
    e.stopPropagation();
    if (!window.confirm(t('employer.dashboard.home.archiveJobConfirm'))) return;
    const archivedAt = new Date().toISOString();
    await supabase.from('jobs').update({ archived_at: archivedAt }).eq('id', jobId);
    await supabase.from('applications').update({ archived_at: archivedAt }).eq('job_id', jobId);
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, archived_at: archivedAt } : j));
    setCandidates(prev => prev.map(c => c.job_id === jobId ? { ...c, archived_at: archivedAt } : c));
  };

  const handleUnarchiveJob = async (jobId, e) => {
    e.stopPropagation();
    await supabase.from('jobs').update({ archived_at: null }).eq('id', jobId);
    await supabase.from('applications').update({ archived_at: null }).eq('job_id', jobId);
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, archived_at: null } : j));
    setCandidates(prev => prev.map(c => c.job_id === jobId ? { ...c, archived_at: null } : c));
  };

  const handleSaveCompanyProfile = async () => {
    if (!companyForm) return;
    const { error } = await supabase.from('employers').update({
      company_name:     companyForm.companyName,
      full_name:        companyForm.fullName,
      job_title:        companyForm.jobTitle,
      company_size:     companyForm.companySize,
      city:             companyForm.city,
      state:            companyForm.state,
      about:            companyForm.about            || null,
      website:          companyForm.website          || null,
      year_founded:     companyForm.yearFounded      ? Number(companyForm.yearFounded) : null,
      industry:         companyForm.industry         || null,
      union_status:     companyForm.companyUnionStatus || null,
      typical_benefits: companyForm.typicalBenefits,
      other_benefits:   companyForm.otherBenefits      || null,
    }).eq('id', employer.id);
    if (error) {
      setCompanyFormMsg(t('employer.dashboard.companyProfile.saveError'));
      setCompanyFormMsgIsError(true);
    } else {
      await supabase.from('jobs').update({ company_name: companyForm.companyName }).eq('employer_id', employer.id);
      setEmployer(prev => ({
        ...prev,
        company_name:     companyForm.companyName,
        full_name:        companyForm.fullName,
        job_title:        companyForm.jobTitle,
        company_size:     companyForm.companySize,
        city:             companyForm.city,
        state:            companyForm.state,
        about:            companyForm.about            || null,
        website:          companyForm.website          || null,
        year_founded:     companyForm.yearFounded      ? Number(companyForm.yearFounded) : null,
        industry:         companyForm.industry         || null,
        union_status:     companyForm.companyUnionStatus || null,
        typical_benefits: companyForm.typicalBenefits,
        other_benefits:   companyForm.otherBenefits      || null,
      }));
      setCompanyFormMsg(t('employer.dashboard.companyProfile.saveSuccess'));
      setCompanyFormMsgIsError(false);
      setTimeout(() => setCompanyFormMsg(''), 3000);
    }
  };

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirmPw) { setPwMsg(t('employer.dashboard.settings.passwordMismatch')); setPwMsgIsSuccess(false); return; }
    if (pwForm.newPw.length < 8) { setPwMsg(t('employer.dashboard.settings.passwordTooShort')); setPwMsgIsSuccess(false); return; }
    const { error } = await supabase.auth.updateUser({ password: pwForm.newPw });
    if (error) {
      setPwMsg(error.message);
      setPwMsgIsSuccess(false);
    } else {
      setPwMsg(t('employer.dashboard.settings.passwordUpdated'));
      setPwMsgIsSuccess(true);
      setPwForm({ newPw: '', confirmPw: '' });
      setTimeout(() => setPwMsg(''), 3000);
    }
  };

  // ── My Profile: this individual's own display name + avatar, distinct from the
  // company logo/name every staff seat otherwise shares -- see handleLogoCropConfirm
  // below for the analogous company-logo flow this mirrors. ──
  const handleSaveProfileName = async () => {
    if (!myUserId || !employer?.id) return;
    setProfileSaving(true);
    setProfileMsg('');
    const trimmed = profileNameForm.trim();
    const { error } = await supabase
      .from('employer_staff')
      .update({ display_name: trimmed || null })
      .eq('employer_id', employer.id)
      .eq('user_id', myUserId);
    setProfileSaving(false);
    if (error) {
      setProfileMsg(t('employer.dashboard.settings.profileNameSaveFailed'));
      setProfileMsgIsError(true);
      return;
    }
    setMyStaffProfile(prev => ({ ...(prev || {}), display_name: trimmed || null }));
    // Keep the Team tab roster in sync -- it's only fetched once on mount, so without this
    // a save here would look reverted/stale if the user checks Team without a full reload.
    setTeamMembers(prev => prev.map(m => m.user_id === myUserId ? { ...m, display_name: trimmed || null } : m));
    setProfileMsg(t('employer.dashboard.settings.profileNameSaved'));
    setProfileMsgIsError(false);
    setTimeout(() => setProfileMsg(''), 2500);
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarCropFile(file);
  };

  const handleAvatarCropConfirm = async (blob, outputType) => {
    setAvatarCropFile(null);
    if (!myUserId || !employer?.id) return;
    setAvatarUploading(true);
    setProfileMsg('');
    const path = `${myUserId}/avatar`;
    const { error: uploadError } = await supabase.storage
      .from('staff-avatars')
      .upload(path, blob, { upsert: true, contentType: outputType });
    if (uploadError) {
      setProfileMsg(t('employer.dashboard.settings.avatarUploadFailed'));
      setProfileMsgIsError(true);
      setAvatarUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('staff-avatars').getPublicUrl(path);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    await supabase.from('employer_staff').update({ avatar_url: publicUrl }).eq('employer_id', employer.id).eq('user_id', myUserId);
    setMyStaffProfile(prev => ({ ...(prev || {}), avatar_url: publicUrl }));
    setTeamMembers(prev => prev.map(m => m.user_id === myUserId ? { ...m, avatar_url: publicUrl } : m));
    setProfileMsg(t('employer.dashboard.settings.avatarSaved'));
    setProfileMsgIsError(false);
    setAvatarUploading(false);
    setTimeout(() => setProfileMsg(''), 2500);
  };

  const setCf = (k, v) => setCompanyForm(p => ({ ...p, [k]: v }));

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCropFile(file);
  };

  const handleLogoCropConfirm = async (blob, outputType) => {
    setCropFile(null);
    setLogoUploading(true);
    setLogoMsg('');
    const path = `${employer.id}/logo`;
    const { error: uploadError } = await supabase.storage
      .from('company-logos')
      .upload(path, blob, { upsert: true, contentType: outputType });
    if (uploadError) {
      setLogoMsg(t('employer.dashboard.companyProfile.logoUploadFailed'));
      setLogoMsgIsError(true);
      setLogoUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('company-logos').getPublicUrl(path);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    await supabase.from('employers').update({ logo_url: publicUrl }).eq('id', employer.id);
    setEmployer(prev => ({ ...prev, logo_url: publicUrl }));
    setLogoMsg(t('employer.dashboard.companyProfile.logoSaved'));
    setLogoMsgIsError(false);
    setLogoUploading(false);
  };

  // Candidate filter derived values
  const uniqueTrades = [...new Set(
    candidates.map(c => c.job?.trade_category).filter(Boolean)
  )].sort();

  const filteredCandidates = candidates.filter(c => {
    const name  = `${c.worker?.first_name || ''} ${c.worker?.last_name || ''}`.toLowerCase();
    const trade = (c.job?.trade_category || '').toLowerCase();
    const status = c.status || 'interested';
    if (candidatesArchiveFilter === 'archived' ? !c.archived_at : !!c.archived_at) return false;
    if (candidateSearch && !name.includes(candidateSearch.toLowerCase()) && !trade.includes(candidateSearch.toLowerCase())) return false;
    if (candidateStatusFilter && status !== candidateStatusFilter) return false;
    if (candidateTradeFilter && c.job?.trade_category !== candidateTradeFilter) return false;
    if (candidateShortlistOnly && !c.shortlisted_at) return false;
    return true;
  });

  // Filtered candidates grouped by job posting, for the Candidates tab's
  // collapsible per-job sections. Preserves the order jobs first appear in
  // (candidates are already fetched newest-first). Within each group,
  // shortlisted candidates float to the top -- Array.prototype.sort is stable,
  // so the existing newest-first order is otherwise untouched.
  const candidateGroups = filteredCandidates.reduce((groups, c) => {
    const jobId = c.job_id || 'unknown';
    let group = groups.find(g => g.jobId === jobId);
    if (!group) {
      group = { jobId, jobTitle: c.job?.job_title || 'Unknown posting', candidates: [] };
      groups.push(group);
    }
    group.candidates.push(c);
    return groups;
  }, []);
  candidateGroups.forEach(g => {
    g.candidates.sort((a, b) => (b.shortlisted_at ? 1 : 0) - (a.shortlisted_at ? 1 : 0));
  });

  // Candidate count per job derived from loaded candidates (excludes archived)
  const candidateCountByJob = candidates.reduce((acc, c) => {
    if (c.archived_at) return acc;
    acc[c.job_id] = (acc[c.job_id] || 0) + 1;
    return acc;
  }, {});

  // Total applicant count per job — mirrors Union Dashboard's apprenticeship
  // applicant count, which does not exclude archived applications.
  const applicantCountByJob = candidates.reduce((acc, c) => {
    acc[c.job_id] = (acc[c.job_id] || 0) + 1;
    return acc;
  }, {});

  // Applicants with at least one verified certification, per job -- same
  // inclusion policy as applicantCountByJob (counts archived too) so the two
  // numbers stay comparable side by side on the job card's stat row.
  const verifiedApplicantCountByJob = candidates.reduce((acc, c) => {
    if (c.has_verified_cert) acc[c.job_id] = (acc[c.job_id] || 0) + 1;
    return acc;
  }, {});

  // Worker dismissal feedback per job — aggregate only, never worker-identifying.
  const dismissalsByJob = jobDismissals.reduce((acc, d) => {
    (acc[d.job_id] = acc[d.job_id] || []).push(d);
    return acc;
  }, {});

  // Which jobs have at least one application created in the last 24h — powers the "new today" trend indicator.
  const hasNewApplicantByJob = candidates.reduce((acc, c) => {
    if (Date.now() - new Date(c.created_at).getTime() < 24 * 60 * 60 * 1000) acc[c.job_id] = true;
    return acc;
  }, {});

  // Jobs derived view for the Jobs tab's Active / Filled / Archived filter
  const jobsForStatus = jobs.filter(j => {
    if (jobsArchiveFilter === 'archived') return !!j.archived_at;
    if (jobsArchiveFilter === 'filled') return j.status === 'filled' && !j.archived_at;
    return j.status === 'active' && !j.archived_at;
  });
  const jobsForView = jobsForStatus
    .filter(j => {
      if (!jobSearchQuery.trim()) return true;
      const q = jobSearchQuery.toLowerCase();
      return (j.job_title || '').toLowerCase().includes(q) || (j.trade_category || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (jobSortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (jobSortBy === 'applicants') return (applicantCountByJob[b.id] || 0) - (applicantCountByJob[a.id] || 0);
      return new Date(b.created_at) - new Date(a.created_at);
    });

  // candidate.offer_response is the most recent job_offers row's status, fetched
  // independently of applications.status. A decline flips applications.status back
  // to 'reviewing' (so the candidate stays actionable -- send another offer, reject,
  // etc.), which took this out of the 'offer_sent' bucket the old logic required, so
  // a declined offer silently displayed as plain "Reviewing". Checking offer_response
  // for 'declined' directly, independent of status, fixes that. The 'pending'/'accepted'
  // sub-cases stay gated to status === 'offer_sent' as before (a freshly-sent, not-yet-
  // answered offer has offer_response === 'pending', not 'sent' -- OFFER_SENT_SUBSTATUS
  // maps that to the 'offer_sent' badge instead of falling through to the default
  // "Interested" badge, which is a second, related bug fixed here in the same pass).
  // Shared by the candidate card and the comparison view so the two can't disagree.
  const OFFER_BLOCKED_STATUSES = ['offer_sent', 'rejected', 'hired', 'withdrawn', 'hire_fell_through'];
  const candidateDisplayStatus = (candidate) => {
    const status = candidate.status || 'interested';
    return candidate.offer_response === 'declined'
      ? 'offer_declined'
      : (status === 'offer_sent' && OFFER_SENT_SUBSTATUS[candidate.offer_response]) || status;
  };

  // Candidate card shared between Dashboard and Candidates tab
  const statusBadge = (status) => {
    if (status === 'reviewing')      return { label: t('employer.dashboard.candidates.statusReviewing'),      color: '#3B82F6' };
    if (status === 'rejected')       return { label: t('employer.dashboard.candidates.statusRejected'),       color: '#DC2626' };
    if (status === 'offer_sent')     return { label: t('employer.dashboard.candidates.statusOfferSent'),      color: '#8B5CF6' };
    if (status === 'offer_accepted') return { label: t('employer.dashboard.candidates.statusOfferAccepted'),  color: '#10B981' };
    if (status === 'offer_declined') return { label: t('employer.dashboard.candidates.statusOfferDeclined'),  color: '#EF4444' };
    if (status === 'hired')          return { label: t('employer.dashboard.candidates.statusHired'),          color: '#10B981' };
    if (status === 'hire_fell_through') return { label: t('employer.dashboard.candidates.statusFellThrough'), color: '#6B7280' };
    return                                   { label: t('employer.dashboard.candidates.statusInterested'),    color: AMBER };
  };

  // Called as a plain function (renderX()), not rendered as a JSX component (<X/>) --
  // it's defined inside this component's body to close over dashboard state/handlers, so
  // invoking it as a component would give React a new function identity every render and
  // it would remount the whole subtree (dropping input focus on every keystroke, the exact
  // bug fixed elsewhere in this file). A plain call has no such component boundary.
  const renderCandidatesErrorBanner = () => (
    <div style={{
      background: 'rgba(226,75,74,0.1)', border: `1px solid ${ATTENTION_RED}55`,
      borderRadius: '8px', padding: '18px 20px', textAlign: 'center',
    }}>
      <p style={{ color: ATTENTION_RED, fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>
        {t('employer.dashboard.candidates.loadError')}
      </p>
      <button
        onClick={() => setRefreshKey(k => k + 1)}
        style={{
          background: 'transparent', border: `1px solid ${ATTENTION_RED}`, color: ATTENTION_RED,
          fontSize: '13px', fontWeight: 700, padding: '7px 16px',
          borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY,
        }}
      >
        {t('common.retry')}
      </button>
    </div>
  );

  // Same reasoning as renderCandidatesErrorBanner above -- plain function call, not JSX.
  // compareEnabled shows the ☆ comparison toggle -- only in the Candidates tab's per-job
  // sections, where a comparison has a single posting to belong to.
  const renderCandidateList = ({ list = candidates, compareEnabled = false }) => (
    candidatesError ? (
      renderCandidatesErrorBanner()
    ) : list.length === 0 ? (
      <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
        {candidates.length === 0 ? t('employer.dashboard.candidates.noneYet') : t('employer.dashboard.candidates.noneMatchFilters')}
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {list.map(candidate => {
          const status = candidate.status || 'interested';
          const { label: badgeLabel, color: badgeColor } = statusBadge(candidateDisplayStatus(candidate));
          const reqCerts     = parseCertsRequired(candidate.job?.certifications_required);
          const workerCerts  = new Set(candidate.worker_cert_names || []);
          const missingCerts = reqCerts.filter(c => !workerCerts.has(c.toLowerCase().trim()));
          const hasMissingCerts = missingCerts.length > 0;
          const isNotifTarget = highlightWorkerId === candidate.worker_id;
          return (
            <div
              key={candidate.id}
              data-worker-id={candidate.worker_id}
              style={{
                background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px',
                boxShadow: isNotifTarget ? `0 0 0 3px ${AMBER}` : 'none', transition: 'box-shadow 0.3s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {compareEnabled && (
                    <CompareToggle
                      selected={candidateCompare.isSelected(candidate.application_id)}
                      onToggle={() => candidateCompare.toggle(candidate.application_id)}
                      addLabel={t('compare.addToCompare')}
                      removeLabel={t('compare.removeFromCompare')}
                    />
                  )}
                  {candidate.profile_photo_url ? (
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
                      <ZoomableImg src={candidate.profile_photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </div>
                  ) : (
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: AMBER, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '500', color: NAVY, flexShrink: 0 }}>
                      {candidate.worker?.first_name?.[0]}{candidate.worker?.last_name?.[0]}
                    </div>
                  )}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '2px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '13px', fontWeight: '500', color: 'white' }}>{candidate.worker?.first_name} {candidate.worker?.last_name}</span>
                      <span style={{ backgroundColor: badgeColor, color: badgeColor === AMBER ? NAVY : WHITE, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}>{badgeLabel}</span>
                      {candidate.shortlisted_at && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(29,158,117,0.15)', color: GREEN, border: `1px solid ${GREEN}`, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}><LineIcon name="star" size={16} filled />{t('employer.dashboard.candidates.shortlistedBadge')}</span>
                      )}
                      {hasMissingCerts && (
                        <span
                          title={t('employer.dashboard.candidates.missingCertsTitle', { certs: missingCerts.join(', ') })}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: 'rgba(245,166,35,0.15)', color: AMBER, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px', cursor: 'help' }}
                        ><LineIcon name="warning" size={16} color={YELLOW} />{t('employer.dashboard.candidates.missingCertsBadge')}</span>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{t('employer.dashboard.candidates.interestedIn', { jobTitle: candidate.job?.job_title })}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '6px', flexShrink: 0, width: isMobile ? '100%' : undefined }}>
                  <button onClick={() => handleMessageCandidate(candidate)} style={{ background: AMBER, border: 'none', color: NAVY, fontSize: '11px', fontWeight: '600', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>{t('employer.dashboard.candidates.messageButton')}</button>
                  <button onClick={() => { navigate(`/worker/profile/${candidate.worker_id}?jobId=${candidate.job_id}`); supabase.from('profile_views').insert({ worker_id: candidate.worker_id, employer_id: employer.id }).then(() => {}); }} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: '11px', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>{t('employer.dashboard.candidates.viewProfileButton')}</button>
                  {status !== 'reviewing' && status !== 'rejected' && status !== 'offer_sent' && status !== 'hired' && status !== 'hire_fell_through' && (
                    <button onClick={() => handleUpdateCandidateStatus(candidate, 'reviewing')} style={{ background: NAVY, border: '1px solid rgba(255,255,255,0.25)', color: WHITE, fontSize: '11px', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>{t('employer.dashboard.candidates.statusReviewing')}</button>
                  )}
                  {/* Send Offer for any active candidate (interested or reviewing, whether or not an
                      interview is scheduled). Not once an offer is out (the "updated offer" button
                      covers that), and not for rejected, hired, withdrawn or fell-through. */}
                  {!OFFER_BLOCKED_STATUSES.includes(status) && (
                    <button onClick={() => openOfferModal(candidate)} style={{ background: '#8B5CF6', border: 'none', color: WHITE, fontSize: '11px', fontWeight: 600, padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>{t('employer.dashboard.candidates.sendOfferButton')}</button>
                  )}
                  {(status === 'offer_sent' || status === 'hired') && (
                    <button onClick={() => handleOpenUpdatedOffer(candidate)} style={{ background: '#8B5CF6', border: 'none', color: WHITE, fontSize: '11px', fontWeight: 600, padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>{t('employer.dashboard.candidates.sendUpdatedOfferButton')}</button>
                  )}
                  {status !== 'rejected' && status !== 'hired' && status !== 'hire_fell_through' && (
                    <button onClick={() => { setRejectModal(candidate); setRejectFeedback(''); }} style={{ background: '#DC2626', border: 'none', color: WHITE, fontSize: '11px', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>{t('employer.dashboard.candidates.rejectButton')}</button>
                  )}
                  {status === 'hired' && jobs.some(j => j.id === candidate.job_id && j.status !== 'filled') && (
                    <button
                      onClick={e => handleMarkFilled(candidate.job_id, e)}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#E09510'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = AMBER; e.currentTarget.style.transform = 'scale(1)'; }}
                      onMouseDown={e => { e.currentTarget.style.transform = 'scale(0.96)'; }}
                      onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                      style={{ background: AMBER, border: 'none', color: NAVY, fontSize: '11px', fontWeight: '600', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', transition: 'background-color 0.15s, transform 0.1s', width: isMobile ? '100%' : undefined }}
                    >{t('employer.dashboard.candidates.markFilledButton')}</button>
                  )}
                  {status === 'hired' && jobs.some(j => j.id === candidate.job_id && j.status !== 'filled') && (
                    <button
                      onClick={() => { if (window.confirm(t('employer.dashboard.candidates.markFellThroughConfirm'))) handleUpdateCandidateStatus(candidate, 'hire_fell_through'); }}
                      style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: '11px', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}
                    >{t('employer.dashboard.candidates.markFellThroughButton')}</button>
                  )}
                  {!candidate.archived_at ? (
                    <button onClick={() => handleArchiveCandidate(candidate.application_id)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.45)', fontSize: '10px', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>{t('employer.dashboard.shared.archiveButton')}</button>
                  ) : (
                    <button onClick={() => handleUnarchiveCandidate(candidate.application_id)} style={{ background: 'transparent', border: `1px solid ${AMBER}`, color: AMBER, fontSize: '10px', padding: '5px 8px', borderRadius: '6px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>{t('employer.dashboard.shared.unarchiveButton')}</button>
                  )}
                </div>
              </div>

              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <button
                  onClick={() => setExpandedCandidateJob(expandedCandidateJob === candidate.id ? null : candidate.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: AMBER, fontFamily: FONT_BODY, padding: 0 }}
                >
                  {expandedCandidateJob === candidate.id ? t('employer.dashboard.candidates.hideJobPosting') : t('employer.dashboard.candidates.viewJobPosting')}
                </button>

                {expandedCandidateJob === candidate.id && (
                  <div style={{ marginTop: '10px' }}>
                    {(candidate.job?.salary_min || candidate.job?.salary_max) && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('employer.dashboard.candidates.payRateLabel')}</span>
                        <span style={{ fontSize: '13px', color: AMBER, fontWeight: 600 }}>${candidate.job.salary_min}–${candidate.job.salary_max}/hr</span>
                      </div>
                    )}
                    {candidate.job?.job_type && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('employer.dashboard.candidates.jobTypeLabel')}</span>
                        <span style={{ fontSize: '13px', color: WHITE }}>{candidate.job.job_type}</span>
                      </div>
                    )}
                    {(candidate.job?.city || candidate.job?.state) && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('employer.dashboard.candidates.locationLabel')}</span>
                        <span style={{ fontSize: '13px', color: WHITE }}>{[candidate.job.city, candidate.job.state].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    {candidate.job?.trade_category && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.trade')}</span>
                        <span style={{ fontSize: '13px', color: WHITE }}>{candidate.job.trade_category}</span>
                      </div>
                    )}
                    {candidate.job?.job_description && (
                      <div>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.jobDescription')}</span>
                        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>{renderJobDescription(candidate.job.job_description)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <CandidateNotes
                initialNote={candidate.employer_notes || ''}
                applicationId={candidate.application_id}
                myUserId={myUserId}
                myDisplayName={myDisplayName}
              />

            </div>
          );
        })}
      </div>
    )
  );

  const companyCompletionItems = [
    { done: !!employer?.logo_url,                                   label: t('employer.dashboard.companyProfile.companyLogoLabel'), tab: 'Company Profile' },
    { done: !!employer?.about,                                      label: t('employer.companyForm.aboutLabel'), tab: 'Company Profile' },
    { done: !!employer?.website,                                    label: t('employer.companyForm.websiteLabel'), tab: 'Company Profile' },
    { done: Array.isArray(employer?.typical_benefits) && employer.typical_benefits.length > 0, label: t('employer.companyForm.typicalBenefitsLabel'), tab: 'Company Profile' },
    { done: !!employer?.industry,                                   label: t('employer.companyForm.industryLabel'), tab: 'Company Profile' },
    { done: !!employer?.company_size,                                label: t('employer.companyForm.companySizeLabel'), tab: 'Company Profile' },
  ];
  const companyCompletedCount = companyCompletionItems.filter(i => i.done).length;
  const companyProfilePct     = Math.round((companyCompletedCount / companyCompletionItems.length) * 100 / 25) * 25;
  const companyMissingItems   = companyCompletionItems.filter(i => !i.done);

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh', overflowX: 'hidden' }}>

      {/* ── Top Navbar ─────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: NAV_H,
        backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', zIndex: 100,
      }}>
        <div onClick={() => { setActiveTab('Dashboard'); setRefreshKey(k => k + 1); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={isMobile ? 28 : 36} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span className="dash-nav-company" style={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>
            {employer?.company_name}
          </span>
          <LanguageToggle userId={employer?.id} />
          <NotificationBell userId={employer?.id} onNavigate={(tab, link) => {
            setActiveTab(tab);
            if (link?.conversation_id) setOpenConversationId(link.conversation_id);
            if (link?.worker_id) setHighlightWorkerId(link.worker_id);
          }} />
          {isMobile && (
            <div onClick={() => setActiveTab('Settings')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: WHITE }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </div>
          )}
          <CompanyAvatar logoUrl={employer?.logo_url} initials={employer?.company_name?.[0] || '?'} size={36} bg={NAVY_MID} textColor={WHITE} />
        </div>
      </nav>

      {/* ── Left Sidebar ────────────────────────────────────── */}
      <aside className="dash-sidebar" style={{
        position: 'fixed', top: NAV_H, left: 0, bottom: 0, width: SIDEBAR_W,
        backgroundColor: NAVY_MID, zIndex: 99,
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>
        <div style={{ padding: '20px 14px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CompanyAvatar logoUrl={employer?.logo_url} initials={employer?.company_name?.[0] || '?'} size={60} bg={NAVY} textColor={WHITE} />
          </div>
          <p style={{ marginTop: '10px', fontWeight: 700, fontSize: '0.88rem', color: WHITE, lineHeight: 1.3 }}>
            {employer?.company_name}
          </p>
          <p style={{ fontSize: '0.77rem', color: 'rgba(255,255,255,0.42)', marginTop: '4px' }}>
            {joinParts([employer?.city, employer?.state], ', ')}
          </p>
          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'center' }}>
            <span style={{
              backgroundColor: 'rgba(29,158,117,0.15)', border: `1px solid ${GREEN}`,
              borderRadius: '999px', padding: '3px 12px',
              fontSize: '0.7rem', fontWeight: 700, color: GREEN,
            }}>
              {t('employer.dashboard.shared.activeStatus')}
            </span>
          </div>
          <div style={{ marginTop: '14px' }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 600, color: companyProfilePct === 100 ? AMBER : 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>
              {companyProfilePct === 100 ? t('employer.dashboard.shared.profileCompleteCheck') : t('employer.dashboard.shared.profilePercentComplete', { pct: companyProfilePct })}
            </p>
            <div style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: `${companyProfilePct}%`, height: '100%', backgroundColor: AMBER, borderRadius: '2px' }} />
            </div>
            {companyMissingItems.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {companyMissingItems.map(item => (
                  <button
                    key={item.label}
                    onClick={() => setActiveTab(item.tab)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '5px', fontFamily: FONT_BODY }}
                  >
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: AMBER, lineHeight: 1 }}>+</span>
                    <span style={{ fontSize: '0.72rem', color: AMBER, fontWeight: 500 }}>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming interview(s) + quick actions */}
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {upcomingInterviews.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {upcomingInterviews.map((interview) => {
                const countdown = getInterviewCountdown(interview.interviewAt, interview.status, now);
                const accentColor = URGENCY_COLORS[countdown.urgency] || AMBER;
                return (
                  <div
                    key={interview.id}
                    onClick={() => openUpcomingInterview(interview)}
                    style={{ backgroundColor: NAVY, borderLeft: `3px solid ${accentColor}`, borderRadius: '0 8px 8px 0', padding: '10px 12px', cursor: 'pointer' }}
                  >
                    <p style={{ fontSize: '0.64rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {t('employer.dashboard.shared.upcomingInterview')}
                    </p>
                    <p style={{ fontSize: '0.78rem', color: WHITE, fontWeight: 600, marginBottom: '2px', overflowWrap: 'break-word' }}>
                      {interview.otherPartyName}
                    </p>
                    <p style={{ fontSize: '0.72rem', color: countdown.urgency === 'normal' ? 'rgba(255,255,255,0.5)' : accentColor, fontWeight: countdown.urgency === 'normal' ? 400 : 700 }}>
                      {countdown.text}
                    </p>
                    {interview.status === 'accepted' && (
                      <p style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.68rem', color: '#10B981', fontWeight: 700, marginTop: '2px' }}><LineIcon name="check" size={16} />{t('employer.dashboard.shared.confirmed')}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={() => navigate('/employer/post-job')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'rgba(255,255,255,0.04)', border: 'none', cursor: 'pointer',
              padding: '9px 10px', borderRadius: '8px', width: '100%',
              color: WHITE, fontFamily: FONT_BODY, fontSize: '0.78rem', fontWeight: 600,
            }}
          >
            <IconBriefcase color={AMBER} />
            {t('employer.dashboard.shared.postJobButton')}
          </button>

          <button
            onClick={() => navigate('/employer/pricing')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'rgba(255,255,255,0.04)', border: 'none', cursor: 'pointer',
              padding: '9px 10px', borderRadius: '8px', width: '100%',
              color: WHITE, fontFamily: FONT_BODY, fontSize: '0.78rem', fontWeight: 600,
            }}
          >
            <IconDollar color={AMBER} />
            {t('employer.dashboard.shared.pricingButton')}
          </button>
        </div>

        {/* Sign out — pinned to bottom of sidebar */}
        <div style={{ marginTop: 'auto', padding: '14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <HelpSidebarButton onClick={support.open} />
          <button
            onClick={async () => { await supabase.auth.signOut(); navigate('/login'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px 10px', borderRadius: '8px', width: '100%',
              color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 600,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            {t('common.signOut')}
          </button>
        </div>

        {/* Legal links — pinned below Sign out */}
        <div style={{ padding: '0 14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <LegalLinksRow fontSize="0.68rem" wrap />
          <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
        </div>
      </aside>

      {/* ── Main Content ─────────────────────────────────────── */}
      <main className="dash-main" style={{
        marginLeft: SIDEBAR_W, marginTop: NAV_H,
        height: `calc(100dvh - ${NAV_H}px)`,
        backgroundColor: NAVY,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>

        {/* Tab bar */}
        <div className="dash-tab-bar" style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, overflowX: 'auto' }}>
          {TABS.map(tab => {
            const msgBadge  = tab === 'Messages' && unreadCount > 0;
            const candBadge = tab === 'Candidates' && newCandidatesCount > 0;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '12px 20px', fontSize: '0.86rem', fontWeight: 600,
                  fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
                  color: activeTab === tab ? AMBER : 'rgba(255,255,255,0.4)',
                  borderBottom: activeTab === tab ? `2px solid ${AMBER}` : '2px solid transparent',
                  marginBottom: '-1px', position: 'relative',
                }}
              >
                {tabLabel(tab)}
                {(msgBadge || candBadge) && (
                  <span style={{ position: 'absolute', top: '6px', right: '2px', minWidth: '16px', height: '16px', borderRadius: '8px', backgroundColor: AMBER, color: NAVY, fontSize: '8px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                    {msgBadge ? (unreadCount > 9 ? '9+' : unreadCount) : (newCandidatesCount > 9 ? '9+' : newCandidatesCount)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {support.modal}

        {/* ── Messages ── */}
        {activeTab === 'Messages' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ flex: 1, overflow: 'hidden' }}>
            <Messaging key={openConversationId || 'msgs'} supabase={supabase} userId={employer.id} userType="employer" actingUserId={myUserId} initialConversationId={openConversationId} support={support} senderName={employer?.company_name} onSendOffer={handleSendOfferFromMessaging} onMobileThreadChange={setChatThreadOpen} onUnreadCountChange={(value, isAbsolute) => {
              if (isAbsolute) {
                setUnreadCount(value);
                setMobileMessageBadge(value);
              } else {
                setUnreadCount(prev => prev + value);
                setMobileMessageBadge(prev => prev + value);
              }
            }} />
            </div>
          </div>
        )}

        {/* ── Candidates ── */}
        {activeTab === 'Candidates' && (
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>

            {/* Active / Archived filter tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '4px', width: 'fit-content' }}>
              {['active', 'archived'].map(f => (
                <button
                  key={f}
                  onClick={() => setCandidatesArchiveFilter(f)}
                  style={{
                    background: candidatesArchiveFilter === f ? AMBER : 'transparent',
                    color: candidatesArchiveFilter === f ? NAVY : 'rgba(255,255,255,0.6)',
                    border: 'none', borderRadius: '6px', padding: '6px 14px',
                    fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY,
                    textTransform: 'capitalize',
                  }}
                >
                  {t(JOB_FILTER_LABEL_KEYS[f])}
                </button>
              ))}
            </div>

            {/* Filter / search row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>

              {/* Search input */}
              <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '170px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder={t('employer.dashboard.candidates.searchPlaceholder')}
                  value={candidateSearch}
                  onChange={e => setCandidateSearch(e.target.value)}
                  style={{
                    width: '100%', paddingLeft: '30px', paddingRight: '10px', paddingTop: '8px', paddingBottom: '8px',
                    background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)',
                    borderRadius: '8px', color: WHITE, fontFamily: FONT_BODY, fontSize: '0.84rem', outline: 'none',
                  }}
                />
              </div>

              {/* Status filter */}
              <select
                value={candidateStatusFilter}
                onChange={e => setCandidateStatusFilter(e.target.value)}
                style={{
                  padding: '8px 10px', background: 'rgba(255,255,255,0.06)',
                  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
                  color: candidateStatusFilter ? WHITE : 'rgba(255,255,255,0.45)',
                  fontFamily: FONT_BODY, fontSize: '0.84rem', outline: 'none', cursor: 'pointer',
                }}
              >
                <option value="">{t('employer.dashboard.candidates.statusAll')}</option>
                <option value="interested">{t('employer.dashboard.candidates.statusInterested')}</option>
                <option value="reviewing">{t('employer.dashboard.candidates.statusReviewing')}</option>
                <option value="offer_sent">{t('employer.dashboard.candidates.statusOfferSent')}</option>
                <option value="hired">{t('employer.dashboard.candidates.statusHired')}</option>
                <option value="hire_fell_through">{t('employer.dashboard.candidates.statusFellThrough')}</option>
                <option value="rejected">{t('employer.dashboard.candidates.statusRejected')}</option>
              </select>

              {/* Trade filter */}
              {uniqueTrades.length > 0 && (
                <select
                  value={candidateTradeFilter}
                  onChange={e => setCandidateTradeFilter(e.target.value)}
                  style={{
                    padding: '8px 10px', background: 'rgba(255,255,255,0.06)',
                    border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
                    color: candidateTradeFilter ? WHITE : 'rgba(255,255,255,0.45)',
                    fontFamily: FONT_BODY, fontSize: '0.84rem', outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="">{t('employer.dashboard.candidates.allTrades')}</option>
                  {uniqueTrades.map(trade => (
                    <option key={trade} value={trade}>{trade}</option>
                  ))}
                </select>
              )}

              {/* Shortlisted only toggle */}
              <button
                onClick={() => setCandidateShortlistOnly(v => !v)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
                  padding: '8px 12px',
                  background: candidateShortlistOnly ? 'rgba(29,158,117,0.15)' : 'rgba(255,255,255,0.06)',
                  border: `1.5px solid ${candidateShortlistOnly ? GREEN : 'rgba(255,255,255,0.12)'}`,
                  borderRadius: '8px',
                  color: candidateShortlistOnly ? GREEN : 'rgba(255,255,255,0.6)',
                  fontFamily: FONT_BODY, fontSize: '0.84rem', fontWeight: 700,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                <LineIcon name="star" size={16} filled={candidateShortlistOnly} />
                {t('employer.dashboard.candidates.shortlistedOnly')}
              </button>

              {/* Results count */}
              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {t('employer.dashboard.candidates.candidateCount', { count: filteredCandidates.length })}
              </span>
            </div>

            {candidatesError ? (
              renderCandidatesErrorBanner()
            ) : candidateGroups.length === 0 ? (
              <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                {candidates.length === 0 ? t('employer.dashboard.candidates.noneYet') : t('employer.dashboard.candidates.noneMatchFilters')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {candidateGroups.map(group => {
                  const isExpanded = !!expandedJobGroups[group.jobId];
                  return (
                    <div key={group.jobId} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
                      <button
                        onClick={() => setExpandedJobGroups(prev => ({ ...prev, [group.jobId]: !prev[group.jobId] }))}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          background: 'rgba(255,255,255,0.03)', border: 'none', cursor: 'pointer',
                          padding: '13px 14px', fontFamily: FONT_BODY, textAlign: 'left',
                        }}
                      >
                        <span style={{ fontSize: '13px', fontWeight: 700, color: WHITE }}>
                          {group.jobTitle} · {t('employer.dashboard.candidates.candidateCount', { count: group.candidates.length })}
                        </span>
                        <span style={{ fontSize: '11px', color: AMBER, fontWeight: 700, flexShrink: 0, marginLeft: '10px' }}>
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </button>
                      {isExpanded && (
                        <div style={{ padding: '12px' }}>
                          <div style={{ marginBottom: '10px' }}>
                            <CompareEntryButton
                              count={group.candidates.filter(c => candidateCompare.isSelected(c.application_id)).length}
                              label={t('compare.employer.entryButton')}
                              onClick={() => setCompareJobId(group.jobId)}
                            />
                          </div>
                          {renderCandidateList({ list: group.candidates, compareEnabled: true })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Company Profile ── */}
        {activeTab === 'Company Profile' && companyForm && (
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('employer.dashboard.companyProfile.header')}
            </p>
            <div style={{ maxWidth: 520 }}>

              {/* Logo upload */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '1.75rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                <CompanyAvatar logoUrl={employer?.logo_url} initials={employer?.company_name?.[0] || '?'} size={80} bg={NAVY} textColor={WHITE} />
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
                <button
                  onClick={() => { setLogoMsg(''); logoInputRef.current?.click(); }}
                  disabled={logoUploading}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 18px', borderRadius: '8px', cursor: logoUploading ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: logoUploading ? 0.7 : 1 }}
                >
                  {logoUploading ? t('employer.dashboard.companyProfile.uploading') : t('employer.dashboard.companyProfile.uploadLogo')}
                </button>
                {logoMsg && (
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: logoMsgIsError ? '#F87171' : GREEN }}>
                    {logoMsg}
                  </span>
                )}
              </div>

              <div style={fld}>
                <label style={lbl}>{t('employer.companyForm.companyNameLabel')}</label>
                <input style={inp} value={companyForm.companyName} onChange={e => setCf('companyName', e.target.value)} />
              </div>
              <div style={g2}>
                <div>
                  <label style={lbl}>{t('employer.companyForm.yourNameLabel')}</label>
                  <input style={inp} value={companyForm.fullName} onChange={e => setCf('fullName', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>{t('employer.companyForm.jobTitleLabel')}</label>
                  <select style={sel} value={companyForm.jobTitle} onChange={e => setCf('jobTitle', e.target.value)}>
                    <option value="">{t('employer.companyForm.jobTitlePlaceholder')}</option>
                    <option value="Owner">{t('employer.companyForm.jobTitleOptions.owner')}</option>
                    <option value="HR Manager">{t('employer.companyForm.jobTitleOptions.hrManager')}</option>
                    <option value="Hiring Manager">{t('employer.companyForm.jobTitleOptions.hiringManager')}</option>
                    <option value="Recruiter">{t('employer.companyForm.jobTitleOptions.recruiter')}</option>
                    <option value="Operations Manager">{t('employer.companyForm.jobTitleOptions.operationsManager')}</option>
                    <option value="Other">{t('employer.companyForm.jobTitleOptions.other')}</option>
                  </select>
                </div>
              </div>
              <div style={fld}>
                <label style={lbl}>{t('employer.companyForm.companySizeLabel')}</label>
                <select style={sel} value={companyForm.companySize} onChange={e => setCf('companySize', e.target.value)}>
                  <option value="">{t('employer.companyForm.companySizePlaceholder')}</option>
                  <option value="1–10 employees">{t('employer.companyForm.companySizeOptions.tier1')}</option>
                  <option value="11–50 employees">{t('employer.companyForm.companySizeOptions.tier2')}</option>
                  <option value="51–200 employees">{t('employer.companyForm.companySizeOptions.tier3')}</option>
                  <option value="200+ employees">{t('employer.companyForm.companySizeOptions.tier4')}</option>
                </select>
              </div>
              <div style={g2}>
                <div>
                  <label style={lbl}>{t('employer.companyForm.cityLabel')}</label>
                  <input style={inp} value={companyForm.city} onChange={e => setCf('city', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>{t('employer.companyForm.stateLabel')}</label>
                  <select style={sel} value={companyForm.state} onChange={e => setCf('state', e.target.value)}>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="WI">Wisconsin</option>
                    <option value="MI">Michigan</option>
                    <option value="OH">Ohio</option>
                    <option value="MO">Missouri</option>
                    <option value="IA">Iowa</option>
                    <option value="MN">Minnesota</option>
                  </select>
                </div>
              </div>
              {/* About the company */}
              <div style={fld}>
                <label style={lbl}>{t('employer.companyForm.aboutLabel')}</label>
                <textarea
                  style={{ ...inp, resize: 'vertical', minHeight: '90px', lineHeight: 1.5 }}
                  placeholder={t('employer.companyForm.aboutPlaceholder')}
                  value={companyForm.about}
                  onChange={e => setCf('about', e.target.value)}
                />
              </div>

              {/* Website */}
              <div style={fld}>
                <label style={lbl}>{t('employer.companyForm.websiteLabel')}</label>
                <input style={inp} placeholder={t('employer.companyForm.websitePlaceholder')} value={companyForm.website} onChange={e => setCf('website', e.target.value)} />
              </div>

              {/* Year founded + Industry */}
              <div style={g2}>
                <div>
                  <label style={lbl}>{t('employer.companyForm.yearFoundedLabel')}</label>
                  <input style={inp} type="number" placeholder={t('employer.companyForm.yearFoundedPlaceholder')} value={companyForm.yearFounded} onChange={e => setCf('yearFounded', e.target.value)} />
                </div>
                <div>
                  <label style={lbl}>{t('employer.companyForm.industryLabel')}</label>
                  <input style={inp} placeholder={t('employer.companyForm.industryPlaceholder')} value={companyForm.industry} onChange={e => setCf('industry', e.target.value)} />
                </div>
              </div>

              {/* Union status */}
              <div style={fld}>
                <label style={lbl}>{t('employer.companyForm.unionStatusLabel')}</label>
                <select style={sel} value={companyForm.companyUnionStatus} onChange={e => setCf('companyUnionStatus', e.target.value)}>
                  <option value="">{t('employer.companyForm.unionStatusPlaceholder')}</option>
                  <option value="Union shop">{t('employer.companyForm.unionStatusOptions.unionShop')}</option>
                  <option value="Non-union">{t('employer.companyForm.unionStatusOptions.nonUnion')}</option>
                  <option value="Open to both">{t('employer.companyForm.unionStatusOptions.openToBoth')}</option>
                </select>
              </div>

              {/* Typical benefits */}
              <div style={fld}>
                <label style={lbl}>{t('employer.companyForm.typicalBenefitsLabel')}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '6px' }}>
                  {BENEFIT_OPTIONS.map(b => (
                    <label key={b} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.84rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                      <input
                        type="checkbox"
                        checked={companyForm.typicalBenefits.includes(b)}
                        onChange={() => setCf('typicalBenefits', companyForm.typicalBenefits.includes(b)
                          ? companyForm.typicalBenefits.filter(x => x !== b)
                          : [...companyForm.typicalBenefits, b]
                        )}
                        style={{ width: '14px', height: '14px', accentColor: AMBER, cursor: 'pointer' }}
                      />
                      {t(`employer.benefitNames.${BENEFIT_KEY_MAP[b]}`)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Other benefits -- free text, for anything not covered by the fixed checkbox list above */}
              <div style={fld}>
                <label style={lbl}>{t('employer.companyForm.otherBenefitsLabel')}</label>
                <input
                  style={inp}
                  placeholder={t('employer.companyForm.otherBenefitsPlaceholder')}
                  value={companyForm.otherBenefits}
                  onChange={e => setCf('otherBenefits', e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '8px' }}>
                <button
                  onClick={handleSaveCompanyProfile}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '10px 22px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                >
                  {t('employer.dashboard.companyProfile.saveChanges')}
                </button>
                {companyFormMsg && (
                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: companyFormMsgIsError ? '#F87171' : GREEN }}>
                    {companyFormMsg}
                  </span>
                )}
              </div>

              {employer?.id && (
                <a
                  href={`/company/${employer.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: '18px', fontSize: '0.85rem', color: AMBER, fontWeight: 600, textDecoration: 'none' }}
                >
                  {t('portfolio.viewPublicCompanyPage')}
                </a>
              )}

              {/* Videos -- employer_portfolio (any active employer_staff seat can manage it) */}
              {employer?.id && (
                <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                  <PortfolioManager
                    table="employer_portfolio"
                    ownerColumn="employer_id"
                    ownerId={employer.id}
                    bucket="employer-videos"
                    subtitle={t('portfolio.employerSubtitle')}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Team: additional employer_staff seats, mirroring the organization Invites tab ── */}
        {activeTab === 'Team' && (
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('employer.dashboard.team.heading')}
            </p>
            <div style={{ maxWidth: 520 }}>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginBottom: '18px', lineHeight: 1.5 }}>
                {t('employer.dashboard.team.subtitle')}
              </p>

              {/* Current plan -- reachable from every seat (not owner-gated like the invite
                  form below), and the one persistent, always-visible entry point into Pricing
                  that works on mobile too (the sidebar's Pricing quick-action is desktop-only,
                  hidden along with the rest of .dash-sidebar under 768px). Team was the natural
                  home since it already surfaces plan-derived seat limits (planUsage). */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                backgroundColor: NAVY_MID, borderRadius: '8px', padding: '12px 14px', marginBottom: '20px',
              }}>
                <div>
                  <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '3px' }}>
                    {t('employer.dashboard.team.currentPlanLabel')}
                  </p>
                  <p style={{ fontSize: '0.9rem', fontWeight: 700, color: WHITE, textTransform: 'capitalize' }}>
                    {planUsage?.has_active_subscription && employer?.plan_tier ? employer.plan_tier : t('employer.dashboard.team.currentPlanNoneValue')}
                  </p>
                </div>
                <span
                  onClick={() => navigate('/employer/pricing')}
                  style={{ color: AMBER, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {t('employer.dashboard.team.viewPricingLink')} →
                </span>
              </div>

              {/* Current members -- pulled straight from employer_staff, so the owner
                  (who never redeemed an invite) shows up here too, not just invited staff. */}
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                {t('employer.dashboard.team.membersHeading')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
                {teamMembers.map(m => {
                  const inactive = m.is_active === false;
                  // The owner can act on anyone but themselves. Self-deactivation is blocked in
                  // the DB trigger regardless; hiding the control keeps them from trying at all.
                  const canToggle = myStaffProfile?.role === 'owner' && m.user_id !== myUserId;
                  return (
                  <div
                    key={m.user_id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                      backgroundColor: NAVY_MID, borderRadius: '8px', padding: '10px 14px',
                      opacity: inactive ? 0.55 : 1,
                    }}
                  >
                    <CompanyAvatar logoUrl={m.avatar_url} initials={(memberName(m) || '?')[0]} size={36} bg={NAVY} textColor={WHITE} />
                    {/* A deactivated row carries three trailing items (Inactive, role, Reactivate).
                        With minWidth:0 they crushed the name to ~13px at 390px instead of wrapping,
                        so the row's flexWrap never kicked in -- this floor makes the badges wrap. */}
                    <div style={{ flex: 1, minWidth: '110px' }}>
                      <div style={{
                        fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        color: inactive ? 'rgba(255,255,255,0.55)' : WHITE,
                        textDecoration: inactive ? 'line-through' : 'none',
                      }}>
                        {memberName(m)}
                      </div>
                    </div>
                    {inactive && (
                      <span style={{
                        fontSize: '0.68rem', fontWeight: 700, color: '#F87171', backgroundColor: 'rgba(248,113,113,0.12)',
                        border: '1px solid rgba(248,113,113,0.3)', borderRadius: '999px', padding: '2px 10px', flexShrink: 0,
                      }}>
                        {t('employer.dashboard.team.inactiveBadge')}
                      </span>
                    )}
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '999px', padding: '2px 10px', flexShrink: 0, textTransform: 'capitalize' }}>
                      {m.role}
                    </span>
                    {canToggle && (
                      <button
                        onClick={() => handleToggleStaffActive(m)}
                        disabled={!!staffActiveSaving[m.user_id]}
                        style={{
                          background: 'transparent', borderRadius: '6px', fontSize: '0.72rem', fontWeight: 700,
                          padding: '5px 10px', flexShrink: 0, fontFamily: FONT_BODY,
                          border: `1px solid ${inactive ? 'rgba(29,158,117,0.45)' : 'rgba(255,255,255,0.2)'}`,
                          color: inactive ? GREEN : 'rgba(255,255,255,0.6)',
                          cursor: staffActiveSaving[m.user_id] ? 'default' : 'pointer',
                          opacity: staffActiveSaving[m.user_id] ? 0.5 : 1,
                        }}
                      >
                        {t(inactive ? 'employer.dashboard.team.reactivateButton' : 'employer.dashboard.team.deactivateButton')}
                      </button>
                    )}
                  </div>
                  );
                })}
                {staffActiveError && (
                  <p style={{
                    fontSize: '0.8rem', color: '#F87171', lineHeight: 1.5,
                    backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
                    borderRadius: '8px', padding: '10px 12px', margin: 0,
                  }}>
                    {staffActiveError}
                  </p>
                )}
              </div>

              {/* Invite generation writes to employer_invites, which is owner-only at the RLS
                  layer (INSERT policy requires employer_staff.role = 'owner') -- hidden here
                  for non-owners so staff never see an action that would just fail. */}
              {myStaffProfile?.role === 'owner' && (
                <>
                  {/* Friendly pre-check only -- the real block is server-side, inside
                      redeem_employer_staff_invite() (employer_can_add_user). This just saves
                      generating a link that's guaranteed to fail redemption. Fails open (shows
                      the form) if usage hasn't loaded yet, since the DB is the real backstop. */}
                  {planUsage && !planUsage.has_active_subscription ? (
                    <div style={{
                      fontSize: '0.85rem', color: '#F87171', lineHeight: 1.5,
                      backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
                      borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', maxWidth: 420,
                    }}>
                      <p style={{ margin: '0 0 8px' }}>{t('employer.dashboard.team.subscriptionRequiredMessage')}</p>
                      <span
                        onClick={() => navigate('/employer/pricing')}
                        style={{ color: AMBER, fontWeight: 700, cursor: 'pointer' }}
                      >{t('employer.postJob.viewPricingButton')}</span>
                    </div>
                  ) : planUsage && planUsage.user_count >= planUsage.user_limit ? (
                    <p style={{
                      fontSize: '0.85rem', color: '#F87171', lineHeight: 1.5,
                      backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
                      borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', maxWidth: 420,
                    }}>
                      {t('employer.dashboard.team.userLimitReachedMessage', { count: planUsage.user_count, limit: planUsage.user_limit })}
                    </p>
                  ) : (
                    <>
                      <div style={{ marginBottom: '10px', maxWidth: 320 }}>
                        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '6px' }}>
                          {t('employer.dashboard.team.inviteEmailLabel')}
                        </label>
                        <input
                          type="email"
                          value={inviteEmailInput}
                          onChange={e => setInviteEmailInput(e.target.value)}
                          placeholder={t('employer.dashboard.team.inviteEmailPlaceholder')}
                          style={{
                            width: '100%', padding: '9px 12px', fontSize: '0.85rem',
                            border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none',
                            backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, colorScheme: 'dark', boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      <button
                        onClick={handleGenerateInvite}
                        disabled={generatingInvite}
                        style={{
                          backgroundColor: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.85rem',
                          padding: '10px 20px', borderRadius: '8px', cursor: generatingInvite ? 'default' : 'pointer',
                          fontFamily: FONT_BODY, opacity: generatingInvite ? 0.7 : 1, marginBottom: '16px',
                        }}
                      >
                        {generatingInvite
                          ? t('employer.dashboard.team.generatingButton')
                          : inviteEmailInput.trim()
                            ? t('employer.dashboard.team.sendInviteButton')
                            : t('employer.dashboard.team.generateButton')}
                      </button>
                    </>
                  )}

                  {inviteErrorMsg && (
                    <p style={{ fontSize: '0.82rem', color: '#F87171', marginBottom: '14px' }}>{inviteErrorMsg}</p>
                  )}

                  {inviteEmailStatusMsg && (
                    <p style={{ fontSize: '0.82rem', color: inviteEmailStatusMsg.isError ? '#F87171' : GREEN, marginBottom: '14px' }}>
                      {inviteEmailStatusMsg.text}
                    </p>
                  )}

                  {lastInviteLink && (
                    <div style={{ background: NAVY_MID, borderRadius: '10px', padding: '14px 16px', marginBottom: '24px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: '10px' }}>
                      <span style={{ flex: 1, fontSize: '0.82rem', color: WHITE, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                        {lastInviteLink}
                      </span>
                      <button
                        onClick={handleCopyInviteLink}
                        style={{
                          background: inviteLinkCopied ? GREEN : 'rgba(245,166,35,0.12)', border: `1px solid ${inviteLinkCopied ? GREEN : AMBER}`,
                          color: inviteLinkCopied ? WHITE : AMBER, fontWeight: 700, fontSize: '0.78rem', padding: '7px 14px',
                          borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, flexShrink: 0, whiteSpace: 'nowrap',
                        }}
                      >
                        {inviteLinkCopied ? t('employer.dashboard.team.copiedButton') : t('employer.dashboard.team.copyButton')}
                      </button>
                    </div>
                  )}
                </>
              )}

              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                {t('employer.dashboard.team.sentInvitesHeading')}
              </p>

              {!invitesLoaded ? (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>{t('organization.dashboard.shared.loading')}</p>
              ) : staffInvites.length === 0 ? (
                <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>{t('employer.dashboard.team.emptyNone')}</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {staffInvites.map(inv => {
                    const joinedMember = inv.redeemed_by ? teamMembers.find(m => m.user_id === inv.redeemed_by) : null;
                    return (
                      <div
                        key={inv.id}
                        style={{
                          display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center',
                          justifyContent: 'space-between', gap: isMobile ? '6px' : '10px',
                          backgroundColor: NAVY_MID, borderRadius: '8px', padding: '12px 14px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: inv.redeemed_by ? GREEN : AMBER, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '999px', padding: '2px 10px', flexShrink: 0 }}>
                            {inv.redeemed_by ? t('employer.dashboard.team.statusJoined') : t('employer.dashboard.team.statusPending')}
                          </span>
                          {joinedMember ? (
                            <span style={{ fontSize: '0.8rem', color: WHITE, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {memberName(joinedMember)}
                            </span>
                          ) : inv.email ? (
                            <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {inv.email}
                            </span>
                          ) : null}
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.38)', flexShrink: 0 }}>
                          {new Date(inv.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Settings ── */}
        {activeTab === 'Settings' && (
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '20px', paddingBottom: '80px' }}>
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>
              {t('common.settings')}
            </p>
            <div style={{ maxWidth: 480 }}>
              {/* My Profile -- this individual's own name/avatar, shown to workers and
                  teammates in place of the shared company identity wherever it's this
                  specific person acting (messages, offers, notes). */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '4px' }}>{t('employer.dashboard.settings.myProfileHeading')}</h3>
                <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.42)', marginBottom: '14px', lineHeight: 1.5 }}>
                  {t('employer.dashboard.settings.myProfileSubtitle')}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                  <CompanyAvatar logoUrl={myStaffProfile?.avatar_url} initials={(myDisplayName || '?')[0]} size={56} bg={NAVY} textColor={WHITE} />
                  <div>
                    <label
                      htmlFor="staff-avatar-input"
                      style={{
                        display: 'inline-block', background: 'transparent', border: '1.5px solid rgba(255,255,255,0.22)',
                        color: WHITE, fontFamily: FONT_BODY, fontSize: '0.8rem', fontWeight: 600,
                        padding: '7px 14px', borderRadius: '7px', cursor: avatarUploading ? 'default' : 'pointer',
                        opacity: avatarUploading ? 0.6 : 1,
                      }}
                    >
                      {avatarUploading ? t('employer.dashboard.settings.avatarUploading') : t('employer.dashboard.settings.avatarChangeButton')}
                    </label>
                    <input id="staff-avatar-input" type="file" accept="image/*" onChange={handleAvatarUpload} disabled={avatarUploading} style={{ display: 'none' }} />
                  </div>
                </div>
                <div style={fld}>
                  <label style={lbl}>{t('employer.dashboard.settings.myNameLabel')}</label>
                  <input style={inp} type="text" placeholder={t('employer.signup.step1.fullNamePlaceholder')} value={profileNameForm} onChange={e => setProfileNameForm(e.target.value)} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    onClick={handleSaveProfileName}
                    disabled={profileSaving}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: profileSaving ? 'default' : 'pointer', fontFamily: FONT_BODY, opacity: profileSaving ? 0.7 : 1 }}
                  >
                    {t('employer.dashboard.settings.saveNameButton')}
                  </button>
                  {profileMsg && (
                    <span style={{ fontSize: '0.84rem', fontWeight: 600, color: profileMsgIsError ? '#F87171' : GREEN }}>
                      {profileMsg}
                    </span>
                  )}
                </div>
              </div>

              {avatarCropFile && (
                <ImageCropModal
                  file={avatarCropFile}
                  onCancel={() => setAvatarCropFile(null)}
                  onConfirm={handleAvatarCropConfirm}
                />
              )}

              {/* Change Password */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '14px' }}>{t('employer.dashboard.settings.changePasswordHeading')}</h3>
                <div style={fld}>
                  <label style={lbl}>{t('employer.dashboard.settings.newPasswordLabel')}</label>
                  <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={pwForm.newPw} onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))} />
                </div>
                <div style={fld}>
                  <label style={lbl}>{t('employer.dashboard.settings.confirmNewPasswordLabel')}</label>
                  <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={pwForm.confirmPw} onChange={e => setPwForm(p => ({ ...p, confirmPw: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    onClick={handleChangePassword}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                  >
                    {t('employer.dashboard.settings.updatePasswordButton')}
                  </button>
                  {pwMsg && (
                    <span style={{ fontSize: '0.84rem', fontWeight: 600, color: pwMsgIsSuccess ? GREEN : '#F87171' }}>
                      {pwMsg}
                    </span>
                  )}
                </div>
              </div>

              {/* Email Notifications */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '14px' }}>{t('employer.dashboard.settings.notificationsHeading')}</h3>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                  <div>
                    <p style={{ fontSize: '0.88rem', fontWeight: 600, color: WHITE }}>{t('employer.dashboard.settings.emailNotificationsLabel')}</p>
                    <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.42)', marginTop: '3px', lineHeight: 1.5 }}>
                      {t('employer.dashboard.settings.emailNotificationsHint')}
                    </p>
                  </div>
                  <div
                    onClick={handleToggleEmailNotifications}
                    style={{ cursor: 'pointer', flexShrink: 0, width: '44px', height: '24px', borderRadius: '12px', backgroundColor: emailNotifications ? AMBER : 'rgba(255,255,255,0.15)', position: 'relative', transition: 'background-color 0.2s' }}
                  >
                    <div style={{ position: 'absolute', top: '2px', left: emailNotifications ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: WHITE, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                  </div>
                </div>
              </div>

              <HelpSettingsCard onClick={support.open} style={{ marginTop: '16px' }} />

              {/* Sign Out */}
              <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', marginTop: '16px' }}>
                <h3 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, marginBottom: '6px' }}>{t('common.signOut')}</h3>
                <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', marginBottom: '14px' }}>{t('worker.settings.signOutNote')}</p>
                <button
                  onClick={async () => { await supabase.auth.signOut(); navigate('/login'); }}
                  style={{
                    background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)',
                    color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                    padding: '9px 22px', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  {t('common.signOut')}
                </button>
              </div>

              {/* Offer Templates */}
              <div style={{ marginTop: '28px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                  <h3 style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', color: WHITE, letterSpacing: '0.06em', margin: 0 }}>{t('employer.dashboard.settings.offerTemplatesHeading')}</h3>
                  {!showTemplateForm && (
                    <button
                      onClick={() => { setShowTemplateForm(true); setEditingTemplate(null); setTemplateForm(TEMPLATE_FORM_BLANK); setTemplateError(''); }}
                      style={{ background: AMBER, border: 'none', color: NAVY, fontFamily: FONT_BODY, fontWeight: 700, fontSize: '0.82rem', padding: '7px 14px', borderRadius: '7px', cursor: 'pointer' }}
                    >{t('employer.dashboard.settings.createTemplateButton')}</button>
                  )}
                </div>

                {templateMsg && (
                  <p style={{ fontSize: '0.84rem', fontWeight: 600, color: GREEN, marginBottom: '10px' }}>{templateMsg}</p>
                )}
                {templateError && (
                  <p role="alert" data-testid="template-error" style={{ fontSize: '0.84rem', fontWeight: 700, color: '#F87171', marginBottom: '10px', overflowWrap: 'anywhere' }}>{templateError}</p>
                )}

                {/* Template list */}
                {!showTemplateForm && (
                  offerTemplates.length === 0 ? (
                    <p style={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' }}>{t('employer.dashboard.settings.noTemplatesYet')}</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '4px' }}>
                      {offerTemplates.map(tpl => (
                        <div key={tpl.id} style={{ background: NAVY_MID, borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: WHITE, marginBottom: '2px' }}>{tpl.template_name}</div>
                            {tpl.job_title && <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.42)' }}>{tpl.job_title}</div>}
                            {tpl.attached_documents?.length > 0 && (
                              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>
                                {t('employer.dashboard.settings.documentsAttached', { count: tpl.attached_documents.length })}
                              </div>
                            )}
                          </div>
                          {deleteTemplateConfirm === tpl.id ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                              <span style={{ fontSize: '0.78rem', color: '#F87171' }}>{t('employer.dashboard.settings.deleteConfirm')}</span>
                              <button onClick={() => handleDeleteTemplate(tpl.id)} style={{ background: '#DC2626', border: 'none', color: WHITE, fontFamily: FONT_BODY, fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '6px', cursor: 'pointer' }}>{t('common.yes')}</button>
                              <button onClick={() => setDeleteTemplateConfirm(null)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, fontSize: '0.75rem', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer' }}>{t('common.no')}</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                              <button
                                onClick={() => { setPendingTemplateId(tpl.id); setActiveTab('Candidates'); setTemplateMsg(t('employer.dashboard.settings.templateReadyMsg', { name: tpl.template_name })); }}
                                style={{ background: AMBER, border: 'none', color: NAVY, fontFamily: FONT_BODY, fontSize: '0.75rem', fontWeight: 700, padding: '5px 10px', borderRadius: '6px', cursor: 'pointer' }}
                              >{t('employer.dashboard.settings.useButton')}</button>
                              <button onClick={() => handleEditTemplate(tpl)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: WHITE, fontFamily: FONT_BODY, fontSize: '0.75rem', fontWeight: 600, padding: '5px 10px', borderRadius: '6px', cursor: 'pointer' }}>{t('employer.dashboard.settings.editButton')}</button>
                              <button onClick={() => setDeleteTemplateConfirm(tpl.id)} style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', fontFamily: FONT_BODY, fontSize: '0.75rem', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer' }}>{t('employer.dashboard.settings.deleteButton')}</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                )}

                {/* Template form */}
                {showTemplateForm && (
                  <div style={{ background: NAVY_MID, borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <h4 style={{ fontFamily: FONT_BODY, fontSize: '0.95rem', fontWeight: 700, color: WHITE, margin: 0 }}>
                      {editingTemplate ? t('employer.dashboard.settings.editTemplateHeading') : t('employer.dashboard.settings.newTemplateHeading')}
                    </h4>

                    {/* Template name */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.templateNameLabel')} <span style={{ color: AMBER }}>*</span></label>
                      <input style={inp} type="text" placeholder={t('employer.dashboard.settings.templateNamePlaceholder')}
                        value={templateForm.templateName}
                        onChange={e => setTemplateForm(f => ({ ...f, templateName: e.target.value }))} />
                    </div>

                    {/* Job title */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.postJob.jobTitleLabel')}</label>
                      <input style={inp} type="text" placeholder={t('employer.dashboard.settings.jobTitleExamplePlaceholder')}
                        value={templateForm.jobTitle}
                        onChange={e => setTemplateForm(f => ({ ...f, jobTitle: e.target.value }))} />
                    </div>

                    {/* Pay */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.offeredPayRateLabel')}</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <select style={{ ...sel, width: 'auto', flexShrink: 0 }} value={templateForm.payType} onChange={e => setTemplateForm(f => ({ ...f, payType: e.target.value }))}>
                          <option value="Hourly">{t('employer.dashboard.settings.payTypeOptions.hourly')}</option>
                          <option value="Salary">{t('employer.dashboard.settings.payTypeOptions.salary')}</option>
                          <option value="Contract">{t('employer.dashboard.settings.payTypeOptions.contract')}</option>
                        </select>
                        <div style={{ display: 'flex', flex: 1, alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', overflow: 'hidden' }}>
                          <span style={{ padding: '9px 6px 9px 12px', color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem' }}>$</span>
                          <input type="number" min="0" value={templateForm.payAmount} onChange={e => setTemplateForm(f => ({ ...f, payAmount: e.target.value }))}
                            placeholder="0" style={{ flex: 1, background: 'none', border: 'none', color: WHITE, outline: 'none', fontSize: '0.88rem', fontFamily: FONT_BODY, padding: '9px 0', minWidth: 0 }} />
                          <span style={{ padding: '9px 12px 9px 4px', color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                            {templateForm.payType === 'Salary' ? t('employer.dashboard.settings.paySuffixYear') : templateForm.payType === 'Contract' ? t('employer.dashboard.settings.paySuffixProject') : t('employer.dashboard.settings.paySuffixHourly')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Pay schedule */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.payScheduleLabel')}</label>
                      <select style={sel} value={templateForm.paySchedule} onChange={e => setTemplateForm(f => ({ ...f, paySchedule: e.target.value }))}>
                        <option value="Weekly">{t('employer.dashboard.settings.payScheduleOptions.weekly')}</option>
                        <option value="Bi-weekly">{t('employer.dashboard.settings.payScheduleOptions.biWeekly')}</option>
                        <option value="Semi-monthly (twice a month)">{t('employer.dashboard.settings.payScheduleOptions.semiMonthly')}</option>
                        <option value="Monthly">{t('employer.dashboard.settings.payScheduleOptions.monthly')}</option>
                      </select>
                    </div>

                    {/* Work location */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.workLocationLabel')}</label>
                      <select style={sel} value={templateForm.workLocation} onChange={e => setTemplateForm(f => ({ ...f, workLocation: e.target.value }))}>
                        <option value="On-site">{t('employer.dashboard.settings.workLocationOptions.onSite')}</option>
                        <option value="Hybrid">{t('employer.dashboard.settings.workLocationOptions.hybrid')}</option>
                        <option value="Remote">{t('employer.dashboard.settings.workLocationOptions.remote')}</option>
                      </select>
                    </div>

                    {/* Work hours */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.workHoursLabel')}</label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.38)', marginBottom: '4px' }}>{t('employer.dashboard.settings.startTimeLabel')}</label>
                          <select style={sel} value={templateForm.workStartTime} onChange={e => setTemplateForm(f => ({ ...f, workStartTime: e.target.value }))}>
                            {WORK_TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.38)', marginBottom: '4px' }}>{t('employer.dashboard.settings.endTimeLabel')}</label>
                          <select style={sel} value={templateForm.workEndTime} onChange={e => setTemplateForm(f => ({ ...f, workEndTime: e.target.value }))}>
                            {WORK_TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
                          const checked = templateForm.workDays.includes(day);
                          return (
                            <label key={day} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: checked ? AMBER : 'rgba(255,255,255,0.45)', fontFamily: FONT_BODY, padding: '5px 10px', border: `1px solid ${checked ? AMBER : 'rgba(255,255,255,0.12)'}`, borderRadius: '6px', backgroundColor: checked ? 'rgba(245,166,35,0.1)' : 'transparent', userSelect: 'none' }}>
                              <input type="checkbox" checked={checked} onChange={() => setTemplateForm(f => ({ ...f, workDays: checked ? f.workDays.filter(d => d !== day) : [...f.workDays, day] }))} style={{ display: 'none' }} />
                              {t(`employer.dashboard.settings.dayAbbrev.${DAY_ABBREV_KEYS[day]}`)}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Overtime */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.overtimeLabel')}</label>
                      <select style={sel} value={templateForm.overtimeAvailability} onChange={e => setTemplateForm(f => ({ ...f, overtimeAvailability: e.target.value }))}>
                        <option value="">{t('employer.dashboard.settings.overtimeSelectPlaceholder')}</option>
                        <option value="Not available">{t('employer.dashboard.settings.overtimeOptions.notAvailable')}</option>
                        <option value="Possible based on workload">{t('employer.dashboard.settings.overtimeOptions.possible')}</option>
                        <option value="Available">{t('employer.dashboard.settings.overtimeOptions.available')}</option>
                        <option value="Required">{t('employer.dashboard.settings.overtimeOptions.required')}</option>
                      </select>
                    </div>

                    {/* Benefits */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.postJob.benefitsLabel')}</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {BENEFIT_OPTIONS.map(b => {
                          const checked = templateForm.benefits.includes(b);
                          return (
                            <label key={b} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.82rem', color: checked ? AMBER : 'rgba(255,255,255,0.55)', fontFamily: FONT_BODY, padding: '5px 10px', border: `1px solid ${checked ? AMBER : 'rgba(255,255,255,0.12)'}`, borderRadius: '6px', backgroundColor: checked ? 'rgba(245,166,35,0.1)' : 'transparent', userSelect: 'none' }}>
                              <input type="checkbox" checked={checked} onChange={() => setTemplateForm(f => ({ ...f, benefits: checked ? f.benefits.filter(x => x !== b) : [...f.benefits, b] }))} style={{ display: 'none' }} />
                              {t(`employer.benefitNames.${BENEFIT_KEY_MAP[b]}`)}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Reporting manager */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.reportingManagerLabel')}</label>
                      <input style={inp} type="text" placeholder={t('employer.dashboard.settings.reportingManagerPlaceholder')}
                        value={templateForm.reportingManager}
                        onChange={e => setTemplateForm(f => ({ ...f, reportingManager: e.target.value }))} />
                    </div>

                    {/* Personal message (offer_templates.personal_message -> the offer's message) */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.offerModal.personalMessageLabel')}</label>
                      <textarea rows={3} value={templateForm.personalMessage} onChange={e => setTemplateForm(f => ({ ...f, personalMessage: e.target.value }))}
                        placeholder={t('employer.dashboard.offerModal.personalMessagePlaceholder')}
                        style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }} />
                    </div>

                    {/* First day instructions */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.firstDayInstructionsLabel')}</label>
                      <textarea rows={4} value={templateForm.firstDayInstructions} onChange={e => setTemplateForm(f => ({ ...f, firstDayInstructions: e.target.value }))}
                        placeholder={t('employer.dashboard.settings.firstDayInstructionsPlaceholder')}
                        style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }} />
                    </div>

                    {/* Attach recurring documents */}
                    <div style={fld}>
                      <label style={lbl}>{t('employer.dashboard.settings.attachDocsLabel')}</label>
                      <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)', margin: '0 0 10px 0', lineHeight: 1.5 }}>
                        {t('employer.dashboard.settings.attachDocsHint')}
                      </p>
                      <input ref={templateDocsInputRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }}
                        onChange={e => {
                          const picked = Array.from(e.target.files || []);
                          if (picked.length) setTemplateDocs(prev => {
                            const names = new Set(prev.map(f => f.name));
                            return [...prev, ...picked.filter(f => !names.has(f.name))];
                          });
                          e.target.value = '';
                        }} />
                      <button type="button" onClick={() => templateDocsInputRef.current?.click()}
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 600, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer' }}>
                        {t('employer.dashboard.settings.choosePdfsButton')}
                      </button>
                      {templateDocUrls.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                          {templateDocUrls.map((url, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '4px 10px' }}>
                              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontFamily: FONT_BODY, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {url.split('/').pop()}
                              </span>
                              <button type="button" onClick={() => setTemplateDocUrls(prev => prev.filter((_, j) => j !== i))}
                                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '0 0 0 2px', lineHeight: 1, fontSize: '14px' }}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                      {templateDocs.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: templateDocUrls.length > 0 ? '6px' : '10px' }}>
                          {templateDocs.map((f, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(245,166,35,0.12)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: '6px', padding: '4px 10px' }}>
                              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontFamily: FONT_BODY, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                              <button type="button" onClick={() => setTemplateDocs(prev => prev.filter((_, j) => j !== i))}
                                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '0 0 0 2px', lineHeight: 1, fontSize: '14px' }}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={handleSaveTemplate}
                        disabled={!templateForm.templateName.trim() || templateSaving || templateDocsUploading}
                        style={{ background: !templateForm.templateName.trim() ? 'rgba(245,166,35,0.4)' : AMBER, border: 'none', color: NAVY, fontFamily: FONT_BODY, fontWeight: 700, fontSize: '0.88rem', padding: '9px 20px', borderRadius: '8px', cursor: !templateForm.templateName.trim() ? 'not-allowed' : 'pointer' }}
                      >{templateSaving || templateDocsUploading ? t('employer.dashboard.settings.savingButton') : t('employer.dashboard.settings.saveTemplateButton')}</button>
                      <button
                        onClick={() => { setShowTemplateForm(false); setEditingTemplate(null); setTemplateForm(TEMPLATE_FORM_BLANK); setTemplateDocs([]); setTemplateDocUrls([]); setTemplateError(''); }}
                        style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 600, padding: '9px 20px', borderRadius: '8px', cursor: 'pointer' }}
                      >{t('common.cancel')}</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Delete or deactivate account -- owner-only, like the union dashboard's: invited staff
                  never see it. The database backs this up (trg_protect_employer_deactivation and
                  delete_own_employer_account() both reject non-owners). */}
              {isEmployerOwner && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '28px', paddingTop: '20px' }}>
                <button
                  onClick={() => setShowDeleteAccountModal(true)}
                  style={{
                    background: 'transparent', border: '1px solid #DC2626', color: '#DC2626',
                    fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700,
                    padding: '9px 22px', borderRadius: '8px', cursor: 'pointer',
                  }}
                >
                  {t('employer.dashboard.settings.deleteOrDeactivateAccountButton')}
                </button>
              </div>
              )}
            </div>
          </div>
        )}

        {showDeleteAccountModal && isEmployerOwner && (
          <DeleteAccountModal
            supabase={supabase}
            userId={employer.id}
            role="employer"
            onClose={() => setShowDeleteAccountModal(false)}
            onContactSupport={() => { setShowDeleteAccountModal(false); setActiveTab('Messages'); support.open(); }}
          />
        )}

        {cropFile && (
          <ImageCropModal
            file={cropFile}
            onCancel={() => setCropFile(null)}
            onConfirm={handleLogoCropConfirm}
          />
        )}

        {/* ── Dashboard ── */}
        {activeTab === 'Dashboard' && (
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', padding: '16px', paddingBottom: '80px' }}>

            {employer?.id && (
              <ProfileCompletionBanner
                storageKey={`profileBannerDismissed_employer_${employer.id}`}
                profileLabel={t('employer.dashboard.shared.companyProfileLabel')}
                missingItems={companyMissingItems}
                pct={companyProfilePct}
                onNavigate={setActiveTab}
              />
            )}

            {/* Upcoming interview(s) -- mobile only, since the sidebar (where this also lives) is hidden below 768px */}
            {isMobile && upcomingInterviews.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {upcomingInterviews.map((interview) => {
                  const countdown = getInterviewCountdown(interview.interviewAt, interview.status, now);
                  const accentColor = URGENCY_COLORS[countdown.urgency] || AMBER;
                  return (
                    <div
                      key={interview.id}
                      onClick={() => openUpcomingInterview(interview)}
                      style={{ backgroundColor: NAVY_MID, borderLeft: `3px solid ${accentColor}`, borderRadius: '0 10px 10px 0', padding: '12px 16px', cursor: 'pointer' }}
                    >
                      <p style={{ fontSize: '0.66rem', fontWeight: 700, color: AMBER, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' }}>
                        {t('employer.dashboard.shared.upcomingInterview')}
                      </p>
                      <p style={{ fontSize: '0.85rem', color: WHITE, fontWeight: 600, marginBottom: '2px' }}>
                        {interview.otherPartyName}
                      </p>
                      <p style={{ fontSize: '0.78rem', color: countdown.urgency === 'normal' ? 'rgba(255,255,255,0.5)' : accentColor, fontWeight: countdown.urgency === 'normal' ? 400 : 700 }}>
                        {countdown.text}
                      </p>
                      {interview.status === 'accepted' && (
                        <p style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', color: '#10B981', fontWeight: 700, marginTop: '2px' }}><LineIcon name="check" size={16} />{t('employer.dashboard.shared.confirmed')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Welcome banner — only when no jobs */}
            {jobs.length === 0 && (
              <div style={{ backgroundColor: NAVY_MID, borderLeft: `3px solid ${AMBER}`, borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <IconBolt color={AMBER} />
                <p style={{ flex: 1, fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500, lineHeight: 1.5 }}>
                  {t('employer.dashboard.home.welcomeBannerText')}
                </p>
                <button
                  onClick={() => navigate('/employer/post-job')}
                  style={{ backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px', padding: '9px 18px', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer', fontFamily: FONT_BODY, flexShrink: 0 }}
                >
                  {t('employer.dashboard.home.postJobArrowButton')}
                </button>
              </div>
            )}

            {/* Stat cards -- each trend is a real computed "+N this week" from created_at
                (or updated_at for Hires, since that reflects when the hire happened, not
                when the worker originally applied), never a hardcoded placeholder. */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '24px' }}>
              <StatCard
                label={t('employer.dashboard.home.statApplicants')}
                value={candidates.length}
                Icon={IconUsers}
                iconColor={AMBER}
                trend={countThisWeek(candidates) > 0 ? t('employer.dashboard.home.trendThisWeek', { count: countThisWeek(candidates) }) : null}
                onClick={() => {
                  setCandidateSearch('');
                  setCandidateStatusFilter('');
                  setCandidateTradeFilter('');
                  setCandidatesArchiveFilter('active');
                  setActiveTab('Candidates');
                }}
              />
              <StatCard
                label={t('employer.dashboard.home.statActiveJobs')}
                value={jobs.filter(j => j.status === 'active' && !j.archived_at).length}
                Icon={IconBriefcase}
                iconColor={AMBER}
                trend={countThisWeek(jobs.filter(j => j.status === 'active')) > 0 ? t('employer.dashboard.home.trendThisWeek', { count: countThisWeek(jobs.filter(j => j.status === 'active')) }) : null}
              />
              <StatCard
                label={t('employer.dashboard.home.statUpcomingInterviews')}
                value={upcomingInterviewsCount}
                Icon={IconCalendar}
                iconColor={AMBER}
                trend={interviewsScheduledThisWeek > 0 ? t('employer.dashboard.home.trendThisWeek', { count: interviewsScheduledThisWeek }) : null}
              />
              <StatCard
                label={t('employer.dashboard.home.statHired')}
                value={candidates.filter(c => c.status === 'hired').length}
                Icon={IconCheck}
                iconColor="#10B981"
                valueColor="#10B981"
                trend={countThisWeek(candidates.filter(c => c.status === 'hired'), 'updated_at') > 0 ? t('employer.dashboard.home.trendThisWeek', { count: countThisWeek(candidates.filter(c => c.status === 'hired'), 'updated_at') }) : null}
                trendColor="#10B981"
              />
            </div>

            {/* Active job postings */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
              <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {t('employer.dashboard.home.jobPostingsHeader')}
              </p>
              <button
                onClick={() => navigate('/employer/post-job')}
                style={{ backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer', fontFamily: FONT_BODY }}
              >
                {t('employer.dashboard.shared.postJobButton')}
              </button>
            </div>

            {/* Active / Filled / Archived filter tabs */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '4px', width: 'fit-content' }}>
              {['active', 'filled', 'archived'].map(f => (
                <button
                  key={f}
                  onClick={() => setJobsArchiveFilter(f)}
                  style={{
                    background: jobsArchiveFilter === f ? AMBER : 'transparent',
                    color: jobsArchiveFilter === f ? NAVY : 'rgba(255,255,255,0.6)',
                    border: 'none', borderRadius: '6px', padding: '6px 14px',
                    fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY,
                    textTransform: 'capitalize',
                  }}
                >
                  {t(JOB_FILTER_LABEL_KEYS[f])}
                </button>
              ))}
            </div>

            {/* Search / sort row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '170px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  placeholder={t('employer.dashboard.home.jobSearchPlaceholder')}
                  value={jobSearchQuery}
                  onChange={e => setJobSearchQuery(e.target.value)}
                  style={{
                    width: '100%', paddingLeft: '30px', paddingRight: '10px', paddingTop: '8px', paddingBottom: '8px',
                    background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)',
                    borderRadius: '8px', color: WHITE, fontFamily: FONT_BODY, fontSize: '0.84rem', outline: 'none',
                  }}
                />
              </div>
              <select
                value={jobSortBy}
                onChange={e => setJobSortBy(e.target.value)}
                style={{
                  padding: '8px 10px', background: 'rgba(255,255,255,0.06)',
                  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
                  color: WHITE, fontFamily: FONT_BODY, fontSize: '0.84rem', outline: 'none', cursor: 'pointer',
                }}
              >
                <option value="newest">{t('employer.dashboard.home.sortNewest')}</option>
                <option value="oldest">{t('employer.dashboard.home.sortOldest')}</option>
                <option value="applicants">{t('employer.dashboard.home.sortApplicants')}</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              {jobsForView.length === 0 ? (
                <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                  {jobsForStatus.length > 0
                    ? t('employer.dashboard.home.noPostingsMatch')
                    : jobsArchiveFilter === 'archived' ? t('employer.dashboard.home.noArchivedJobs') : jobsArchiveFilter === 'filled' ? t('employer.dashboard.home.noFilledJobs') : t('employer.dashboard.home.noActiveJobs')}
                </div>
              ) : (
                jobsForView.map(job => {
                  const isFilled    = job.status === 'filled';
                  const isArchived  = !!job.archived_at;
                  const interestedN = candidateCountByJob[job.id] || 0;
                  const applicantN  = applicantCountByJob[job.id] || 0;
                  const verifiedN   = verifiedApplicantCountByJob[job.id] || 0;
                  const daysOpen       = Math.floor((Date.now() - new Date(job.created_at).getTime()) / (24 * 60 * 60 * 1000));
                  const isResolved     = isFilled || isArchived;
                  const needsAttention = !isResolved && applicantN === 0 && daysOpen > 14;
                  const accentColor    = (isResolved || applicantN > 0) ? GREEN : (needsAttention ? ATTENTION_RED : AMBER);
                  const hasNewToday    = !!hasNewApplicantByJob[job.id];
                  const dismissalsForJob = dismissalsByJob[job.id] || [];
                  const dismissalReasonCounts = dismissalsForJob.reduce((acc, d) => {
                    const key = d.reason || t('employer.dashboard.home.noReasonGiven');
                    acc[key] = (acc[key] || 0) + 1;
                    return acc;
                  }, {});
                  const dismissalOtherNotes = dismissalsForJob.filter(d => d.reason === 'Other' && d.reason_detail).map(d => d.reason_detail);
                  return (
                    <div key={job.id} style={{ background: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderLeft: `3px solid ${accentColor}`, borderRadius: '0 8px 8px 0', padding: '12px', cursor: 'pointer', opacity: (isFilled || isArchived) ? 0.7 : 1 }} onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', fontWeight: '500', color: 'white' }}>{job.job_title}</span>
                            {job.urgently_hiring && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 700, color: WHITE, backgroundColor: ATTENTION_RED, borderRadius: '999px', padding: '2px 8px' }}><LineIcon name="flame" size={16} color={YELLOW} />{t('employer.dashboard.home.urgentlyHiringBadge')}</span>
                            )}
                            {isFilled && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: WHITE, backgroundColor: GREEN, borderRadius: '999px', padding: '2px 8px' }}>{t('employer.dashboard.home.filledBadge')}</span>
                            )}
                            {isArchived && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '999px', padding: '2px 8px' }}>{t('employer.dashboard.home.archivedBadge')}</span>
                            )}
                            {needsAttention && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: ATTENTION_RED_TEXT, backgroundColor: 'rgba(226,75,74,0.15)', borderRadius: '999px', padding: '2px 8px' }}>{t('employer.dashboard.home.needsAttentionBadge')}</span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '6px' }}>{joinParts([joinParts([job.city, job.state], ', '), t('employer.dashboard.home.postedOn', { date: new Date(job.created_at).toLocaleDateString() })])}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '11px', color: AMBER, fontWeight: '500' }}>${job.salary_min}–${job.salary_max}/hr</span>
                            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>{joinParts([job.job_type, job.union_status])}</span>
                            {job.shift && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{job.shift}</span>
                            )}
                            {job.per_diem && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('employer.dashboard.home.perDiemBadge')}</span>
                            )}
                            {job.travel_required && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('employer.dashboard.home.travelRequiredBadge')}</span>
                            )}
                            {Array.isArray(job.benefits) && job.benefits.length > 0 && (
                              <span style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.65)', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '2px 8px' }}>{t('employer.dashboard.home.benefitsCount', { count: job.benefits.length })}</span>
                            )}
                            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>{t('employer.dashboard.home.applicantCount', { count: applicantN })}</span>
                            {hasNewToday && (
                              <span style={{ fontSize: '11px', color: TREND_GREEN, fontWeight: 600 }}>{t('employer.dashboard.home.newTodayBadge')}</span>
                            )}
                            {needsAttention && (
                              <span style={{ fontSize: '11px', color: ATTENTION_RED_TEXT, fontWeight: 600 }}>{t('employer.dashboard.home.daysOpenBadge', { count: daysOpen })}</span>
                            )}
                            {interestedN > 0 && (
                              <span style={{ fontSize: '10px', color: AMBER, fontWeight: 600 }}>{t('employer.dashboard.home.interestedCount', { count: interestedN })}</span>
                            )}
                          </div>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '6px' }}>
                            {daysOpen <= 0 ? t('employer.dashboard.home.justPostedStat') : t('employer.dashboard.home.daysLiveStat', { count: daysOpen })}
                            {' · '}
                            {t('employer.dashboard.home.applicantCount', { count: applicantN })}
                            {applicantN > 0 && (
                              <>
                                {' · '}
                                <span style={{ color: verifiedN > 0 ? GREEN : 'rgba(255,255,255,0.4)', fontWeight: verifiedN > 0 ? 700 : 400 }}>
                                  {t('employer.dashboard.home.verifiedApplicantsStat', { count: verifiedN })}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginLeft: '10px' }}>{expandedJob === job.id ? '▲' : '▼'}</span>
                      </div>

                      {expandedJob === job.id && (
                        <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '16px' }}>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.trade')}</div>
                          <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.trade_category}</div>

                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('employer.dashboard.home.experienceRequiredLabel')}</div>
                          <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.min_years_experience}</div>

                          {job.certifications_required && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.certsRequired')}</div>
                              <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.certifications_required}</div>
                            </>
                          )}

                          {Array.isArray(job.certifications_preferred) && job.certifications_preferred.length > 0 && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.certsPreferred')}</div>
                              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', marginBottom: '12px' }}>{job.certifications_preferred.join(', ')}</div>
                            </>
                          )}

                          {job.per_diem && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('employer.dashboard.home.perDiemLabel')}</div>
                              <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.per_diem_amount || t('common.yes')}</div>
                            </>
                          )}

                          {job.travel_required && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('employer.dashboard.home.travelLabel')}</div>
                              <div style={{ fontSize: '13px', color: 'white', marginBottom: '12px' }}>{job.travel_details || t('employer.dashboard.home.requiredFallback')}</div>
                            </>
                          )}

                          {Array.isArray(job.benefits) && job.benefits.length > 0 && (
                            <>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('employer.postJob.benefitsLabel')}</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                                {job.benefits.map(b => (
                                  <span key={b} style={{ fontSize: '11px', color: 'white', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '999px', padding: '3px 10px' }}>{b}</span>
                                ))}
                              </div>
                            </>
                          )}

                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('worker.companyProfile.jobDescription')}</div>
                          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px', lineHeight: '1.6' }}>{renderJobDescription(job.job_description)}</div>

                          {dismissalsForJob.length > 0 && (
                            <div style={{ marginBottom: '16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px 14px' }}>
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('employer.dashboard.home.whyWorkersPassing')}</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: dismissalOtherNotes.length > 0 ? '8px' : 0 }}>
                                {Object.entries(dismissalReasonCounts).map(([reason, count]) => (
                                  <div key={reason} style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}>
                                    <span style={{ color: WHITE, fontWeight: 700 }}>{count}</span> — {reason}
                                  </div>
                                ))}
                              </div>
                              {dismissalOtherNotes.length > 0 && (
                                <>
                                  <button
                                    onClick={e => { e.stopPropagation(); setExpandedFeedbackJob(expandedFeedbackJob === job.id ? null : job.id); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: AMBER, fontFamily: FONT_BODY, padding: 0 }}
                                  >
                                    {expandedFeedbackJob === job.id ? t('employer.dashboard.home.hideFeedback') : t('employer.dashboard.home.seeFeedback', { count: dismissalOtherNotes.length })}
                                  </button>
                                  {expandedFeedbackJob === job.id && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                                      {dismissalOtherNotes.map((note, i) => (
                                        <div key={i} style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', borderLeft: '2px solid rgba(255,255,255,0.15)', paddingLeft: '8px' }}>
                                          &ldquo;{note}&rdquo;
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={e => { e.stopPropagation(); navigate(`/employer/edit-job/${job.id}`); }}
                              style={{ background: AMBER, border: 'none', color: NAVY, fontSize: '11px', fontWeight: '500', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}
                            >{t('employer.dashboard.shared.editJobButton')}</button>
                            <button
                              onClick={e => { e.stopPropagation(); navigate('/employer/post-job', { state: { duplicateJob: job } }); }}
                              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: '11px', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}
                            >{t('employer.dashboard.home.duplicateButton')}</button>
                            <button
                              onClick={e => e.stopPropagation()}
                              style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: '11px', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}
                            >{t('employer.dashboard.home.viewCandidatesButton')}</button>
                            {!isFilled && (
                              <button
                                onClick={e => handleMarkFilled(job.id, e)}
                                style={{ background: 'transparent', border: '1px solid rgba(29,158,117,0.4)', color: GREEN, fontSize: '11px', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}
                              >{t('employer.dashboard.home.markFilledButton')}</button>
                            )}
                            {isFilled && (
                              <button
                                onClick={e => handleReopenJob(job.id, e)}
                                style={{ background: NAVY, border: '1px solid rgba(255,255,255,0.25)', color: WHITE, fontSize: '11px', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}
                              >{t('employer.dashboard.home.reopenPositionButton')}</button>
                            )}
                            {!isArchived ? (
                              <button
                                onClick={e => handleArchiveJob(job.id, e)}
                                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', fontSize: '11px', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}
                              >{t('employer.dashboard.shared.archiveButton')}</button>
                            ) : (
                              <button
                                onClick={e => handleUnarchiveJob(job.id, e)}
                                style={{ background: 'transparent', border: `1px solid ${AMBER}`, color: AMBER, fontSize: '11px', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}
                              >{t('employer.dashboard.shared.unarchiveButton')}</button>
                            )}
                            <button
                              onClick={e => handleDeleteJob(job.id, e)}
                              style={{ background: 'transparent', border: '1px solid rgba(248,113,113,0.35)', color: '#F87171', fontSize: '11px', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer' }}
                            >{t('employer.dashboard.shared.deleteButton')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Recent candidates */}
            <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
              {t('employer.dashboard.home.recentCandidatesHeader')}
            </p>
            <div style={{ marginBottom: '28px' }}>
              {renderCandidateList({ list: candidates.filter(c => !c.archived_at) })}
            </div>

          </div>
        )}

        {isMobile && !(activeTab === 'Messages' && chatThreadOpen) && (
          <div style={{ padding: '20px 16px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <LegalLinksRow fontSize="0.7rem" />
            <p style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
          </div>
        )}
      </main>

      {/* ── Mobile Bottom Nav ── */}
      <nav className="dash-bottom-nav" style={{
        display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 'calc(58px + env(safe-area-inset-bottom))', paddingBottom: 'env(safe-area-inset-bottom)', boxSizing: 'border-box',
        backgroundColor: NAVY_MID, borderTop: '1px solid rgba(255,255,255,0.08)',
        zIndex: 200, alignItems: 'center', justifyContent: 'space-around',
      }}>
        {[
          { tab: 'Dashboard',       label: 'Dashboard',  Icon: IconGrid     },
          { tab: 'Candidates',      label: 'Candidates', Icon: IconUsers    },
          { tab: 'Messages',        label: 'Messages',   Icon: IconMsg      },
          { tab: 'Company Profile', label: 'Company',    Icon: IconBuilding },
          { tab: 'Team',            label: 'Team',       Icon: IconUserPlus },
          { tab: 'Settings',        label: 'Settings',   Icon: IconSettings },
        ].map(({ tab, label, Icon }) => {
          const isActive  = activeTab === tab;
          const badgeCount = tab === 'Messages' ? mobileMessageBadge : tab === 'Candidates' ? newCandidatesCount : 0;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              aria-current={isActive ? 'page' : undefined}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', padding: '4px 6px', cursor: 'pointer', flex: 1, position: 'relative', background: 'none', border: 'none', margin: 0, font: 'inherit' }}
            >
              <Icon color={isActive ? AMBER : 'rgba(255,255,255,0.38)'} />
              {badgeCount > 0 && (
                <span style={{ position: 'absolute', top: 0, left: '50%', marginLeft: '4px', minWidth: '15px', height: '15px', borderRadius: '8px', backgroundColor: AMBER, color: NAVY, fontSize: '8px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', lineHeight: 1 }}>
                  {badgeCount > 9 ? '9+' : badgeCount}
                </span>
              )}
              <span style={{ fontSize: '0.56rem', fontWeight: 600, color: isActive ? AMBER : 'rgba(255,255,255,0.38)', textAlign: 'center' }}>
                {tabLabel(tab)}
              </span>
            </button>
          );
        })}
      </nav>

      {/* ── Send Offer Modal ── */}
      {offerModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.65)', padding: '16px', overflowX: 'hidden' }}>
          <div style={{ backgroundColor: NAVY_MID, borderRadius: '16px', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', display: 'flex', flexDirection: 'column' }}>
            {/* Sticky header */}
            <div style={{ padding: isMobile ? '20px 16px 0' : '28px 32px 0', flexShrink: 0 }}>
              <h2 style={{ fontFamily: FONT_HEAD, fontSize: '2rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '4px' }}>{t('employer.dashboard.offerModal.heading')}</h2>
              <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', marginBottom: '20px' }}>
                {t('employer.dashboard.offerModal.toPrefix')} {offerModal.worker?.first_name} {offerModal.worker?.last_name}
              </p>
            </div>

            {offerSentId === offerModal.application_id ? (
              <div style={{ textAlign: 'center', padding: isMobile ? '40px 16px' : '40px 32px' }}>
                <LineIcon name="checkCircle" size={40} color={GREEN} style={{ margin: '0 auto 10px' }} />
                <p style={{ fontSize: '1rem', fontWeight: 700, color: GREEN, fontFamily: FONT_BODY }}>{t('employer.dashboard.offerModal.offerSentMsg')}</p>
              </div>
            ) : (
              <div style={{ padding: isMobile ? '0 16px 20px' : '0 32px 28px', display: 'flex', flexDirection: 'column', gap: '14px', overflowX: 'hidden' }}>
                {/* Load Template */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.offerModal.loadTemplateLabel')}</label>
                  {offerTemplates.length === 0 ? (
                    <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.32)', margin: 0 }}>{t('employer.dashboard.offerModal.noTemplatesHint')}</p>
                  ) : (
                    <select
                      value={loadTemplateId}
                      onChange={e => {
                        const id = e.target.value;
                        setLoadTemplateId(id);
                        if (!id) { setOfferTemplateDocUrls([]); return; }
                        const tpl = offerTemplates.find(tpl => tpl.id === id);
                        if (!tpl) return;
                        setOfferForm(f => ({ ...f, ...templateToOfferFields(tpl) }));
                        if (tpl.attached_documents?.length) setOfferTemplateDocUrls(tpl.attached_documents);
                      }}
                      style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, cursor: 'pointer' }}
                    >
                      <option value="">{t('employer.dashboard.offerModal.selectTemplatePlaceholder')}</option>
                      {offerTemplates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.template_name}</option>)}
                    </select>
                  )}
                  {loadTemplateId && (
                    <p style={{ fontSize: '0.78rem', color: AMBER, marginTop: '6px', margin: '6px 0 0 0' }}>
                      {t('employer.dashboard.offerModal.templateLoadedHint')}
                    </p>
                  )}
                </div>

                {/* Job title */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.postJob.jobTitleLabel')}</label>
                  <input type="text" value={offerForm.jobTitle} onChange={e => setOfferForm(f => ({ ...f, jobTitle: e.target.value }))}
                    placeholder={t('employer.dashboard.settings.jobTitleExamplePlaceholder')}
                    style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY }} />
                </div>

                {/* Pay rate + schedule */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.settings.offeredPayRateLabel')}</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                      <select value={offerForm.payType} onChange={e => setOfferForm(f => ({ ...f, payType: e.target.value }))}
                        style={{ padding: '9px 10px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, cursor: 'pointer', flexShrink: 0 }}>
                        <option value="Hourly">{t('employer.dashboard.settings.payTypeOptions.hourly')}</option>
                        <option value="Salary">{t('employer.dashboard.settings.payTypeOptions.salary')}</option>
                        <option value="Contract">{t('employer.dashboard.settings.payTypeOptions.contract')}</option>
                      </select>
                      <div style={{ display: 'flex', flex: 1, alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', overflow: 'hidden' }}>
                        <span style={{ padding: '9px 6px 9px 12px', color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', flexShrink: 0 }}>$</span>
                        <input type="number" min="0" value={offerForm.payAmount} onChange={e => setOfferForm(f => ({ ...f, payAmount: e.target.value }))}
                          placeholder="0"
                          style={{ flex: 1, background: 'none', border: 'none', color: WHITE, outline: 'none', fontSize: '0.88rem', fontFamily: FONT_BODY, padding: '9px 0', minWidth: 0 }} />
                        <span style={{ padding: '9px 12px 9px 4px', color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          {offerForm.payType === 'Salary' ? t('employer.dashboard.settings.paySuffixYear') : offerForm.payType === 'Contract' ? t('employer.dashboard.settings.paySuffixProject') : t('employer.dashboard.settings.paySuffixHourly')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.settings.payScheduleLabel')}</label>
                    <select value={offerForm.paySchedule} onChange={e => setOfferForm(f => ({ ...f, paySchedule: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, cursor: 'pointer' }}>
                      <option value="Weekly">{t('employer.dashboard.settings.payScheduleOptions.weekly')}</option>
                      <option value="Bi-weekly">{t('employer.dashboard.settings.payScheduleOptions.biWeekly')}</option>
                      <option value="Semi-monthly (twice a month)">{t('employer.dashboard.settings.payScheduleOptions.semiMonthly')}</option>
                      <option value="Monthly">{t('employer.dashboard.settings.payScheduleOptions.monthly')}</option>
                    </select>
                  </div>
                </div>

                {/* Start date + work location */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                  <div style={isMobile ? { overflow: 'hidden' } : undefined}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.offerModal.startDateLabel')}</label>
                    <input type="date" value={offerForm.startDate} onChange={e => setOfferForm(f => ({ ...f, startDate: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, colorScheme: 'dark', ...(isMobile ? { maxWidth: '100%', boxSizing: 'border-box' } : {}) }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.settings.workLocationLabel')}</label>
                    <select value={offerForm.workLocation} onChange={e => setOfferForm(f => ({ ...f, workLocation: e.target.value }))}
                      style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, cursor: 'pointer' }}>
                      <option value="On-site">{t('employer.dashboard.settings.workLocationOptions.onSite')}</option>
                      <option value="Hybrid">{t('employer.dashboard.settings.workLocationOptions.hybrid')}</option>
                      <option value="Remote">{t('employer.dashboard.settings.workLocationOptions.remote')}</option>
                    </select>
                  </div>
                </div>

                {/* Work hours */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '8px' }}>{t('employer.dashboard.settings.workHoursLabel')}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.38)', marginBottom: '4px' }}>{t('employer.dashboard.settings.startTimeLabel')}</label>
                      <select value={offerForm.workStartTime} onChange={e => setOfferForm(f => ({ ...f, workStartTime: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', fontSize: '0.86rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, cursor: 'pointer' }}>
                        {WORK_TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.72rem', color: 'rgba(255,255,255,0.38)', marginBottom: '4px' }}>{t('employer.dashboard.settings.endTimeLabel')}</label>
                      <select value={offerForm.workEndTime} onChange={e => setOfferForm(f => ({ ...f, workEndTime: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', fontSize: '0.86rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, cursor: 'pointer' }}>
                        {WORK_TIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => {
                      const checked = offerForm.workDays.includes(day);
                      return (
                        <label key={day} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: checked ? AMBER : 'rgba(255,255,255,0.45)', fontFamily: FONT_BODY, padding: '5px 10px', border: `1px solid ${checked ? AMBER : 'rgba(255,255,255,0.12)'}`, borderRadius: '6px', backgroundColor: checked ? 'rgba(245,166,35,0.1)' : 'transparent', userSelect: 'none' }}>
                          <input type="checkbox" checked={checked} onChange={() => setOfferForm(f => ({ ...f, workDays: checked ? f.workDays.filter(d => d !== day) : [...f.workDays, day] }))} style={{ display: 'none' }} />
                          {t(`employer.dashboard.settings.dayAbbrev.${DAY_ABBREV_KEYS[day]}`)}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Overtime availability */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.settings.overtimeLabel')}</label>
                  <select value={offerForm.overtimeAvailability} onChange={e => setOfferForm(f => ({ ...f, overtimeAvailability: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, cursor: 'pointer' }}>
                    <option value="">{t('employer.dashboard.settings.overtimeSelectPlaceholder')}</option>
                    <option value="Not available">{t('employer.dashboard.settings.overtimeOptions.notAvailable')}</option>
                    <option value="Possible based on workload">{t('employer.dashboard.settings.overtimeOptions.possible')}</option>
                    <option value="Available">{t('employer.dashboard.settings.overtimeOptions.available')}</option>
                    <option value="Required">{t('employer.dashboard.settings.overtimeOptions.required')}</option>
                  </select>
                </div>

                {/* Reporting manager */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.settings.reportingManagerLabel')}</label>
                  <input type="text" value={offerForm.reportingManager} onChange={e => setOfferForm(f => ({ ...f, reportingManager: e.target.value }))}
                    placeholder={t('employer.dashboard.settings.reportingManagerPlaceholder')}
                    style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY }} />
                </div>

                {/* Benefits */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '8px' }}>{t('employer.postJob.benefitsLabel')}</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {BENEFIT_OPTIONS.map(b => {
                      const checked = offerForm.benefits.includes(b);
                      return (
                        <label key={b} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.82rem', color: checked ? AMBER : 'rgba(255,255,255,0.55)', fontFamily: FONT_BODY, padding: '5px 10px', border: `1px solid ${checked ? AMBER : 'rgba(255,255,255,0.12)'}`, borderRadius: '6px', backgroundColor: checked ? 'rgba(245,166,35,0.1)' : 'transparent', userSelect: 'none' }}>
                          <input type="checkbox" checked={checked} onChange={() => setOfferForm(f => ({ ...f, benefits: checked ? f.benefits.filter(x => x !== b) : [...f.benefits, b] }))} style={{ display: 'none' }} />
                          {t(`employer.benefitNames.${BENEFIT_KEY_MAP[b]}`)}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Personal message */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.offerModal.personalMessageLabel')}</label>
                  <textarea rows={3} value={offerForm.message} onChange={e => setOfferForm(f => ({ ...f, message: e.target.value }))}
                    placeholder={t('employer.dashboard.offerModal.personalMessagePlaceholder')}
                    style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, resize: 'vertical', lineHeight: 1.5 }} />
                </div>

                {/* First day instructions */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' }}>{t('employer.dashboard.offerModal.firstDayInstructionsLabel')}</label>
                  <textarea rows={4} value={offerForm.firstDayInstructions} onChange={e => setOfferForm(f => ({ ...f, firstDayInstructions: e.target.value }))}
                    placeholder={t('employer.dashboard.offerModal.firstDayInstructionsPlaceholder')}
                    style={{ width: '100%', padding: '9px 12px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY, resize: 'vertical', lineHeight: 1.5 }} />
                </div>

                {/* Attach documents */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '8px' }}>{t('employer.dashboard.offerModal.attachDocsLabel')}</label>
                  <input ref={docsInputRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }}
                    onChange={e => {
                      const picked = Array.from(e.target.files || []);
                      if (picked.length) setAttachedDocs(prev => {
                        const names = new Set(prev.map(f => f.name));
                        return [...prev, ...picked.filter(f => !names.has(f.name))];
                      });
                      e.target.value = '';
                    }} />
                  <button type="button" onClick={() => docsInputRef.current?.click()}
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', fontFamily: FONT_BODY, fontSize: '0.82rem', fontWeight: 600, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}>
                    {t('employer.dashboard.settings.choosePdfsButton')}
                  </button>
                  {offerTemplateDocUrls.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                      {offerTemplateDocUrls.map((url, i) => (
                        <div key={`tpl-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '4px 10px' }}>
                          <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontFamily: FONT_BODY, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {url.split('/').pop()}
                          </span>
                          <button type="button" onClick={() => setOfferTemplateDocUrls(prev => prev.filter((_, j) => j !== i))}
                            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '0 0 0 2px', lineHeight: 1, fontSize: '14px' }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {attachedDocs.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                      {attachedDocs.map((f, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '4px 10px' }}>
                          <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontFamily: FONT_BODY, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                          <button type="button" onClick={() => setAttachedDocs(prev => prev.filter((_, j) => j !== i))}
                            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '0 0 0 2px', lineHeight: 1, fontSize: '14px' }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', justifyContent: isMobile ? 'stretch' : 'flex-end', paddingTop: '4px' }}>
                  <button
                    onClick={() => { setOfferModal(null); setOfferForm(OFFER_FORM_BLANK); setAttachedDocs([]); setOfferTemplateDocUrls([]); setLoadTemplateId(''); }}
                    style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.6)', fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 600, padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}
                  >{t('common.cancel')}</button>
                  <button
                    onClick={handleSendOffer}
                    disabled={offerSending || docsUploading}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700, padding: '10px 24px', borderRadius: '8px', cursor: (offerSending || docsUploading) ? 'default' : 'pointer', opacity: (offerSending || docsUploading) ? 0.7 : 1, width: isMobile ? '100%' : undefined }}
                  >{offerSending ? t('employer.dashboard.offerModal.sendingButton') : t('employer.dashboard.offerModal.sendOfferButton')}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Reject Candidate Modal ── */}
      {compareJobId && (() => {
        const compared = candidates.filter(c => c.job_id === compareJobId && candidateCompare.isSelected(c.application_id));
        return (
          <EmployerCandidateCompare
            employer={employer}
            jobTitle={candidates.find(c => c.job_id === compareJobId)?.job?.job_title || ''}
            candidates={compared}
            statusLabel={c => statusBadge(candidateDisplayStatus(c)).label}
            onRemove={candidateCompare.remove}
            onClear={() => compared.forEach(c => candidateCompare.remove(c.application_id))}
            onClose={() => setCompareJobId(null)}
          />
        );
      })()}

      {rejectModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.65)', padding: '16px' }}>
          <div style={{ backgroundColor: WHITE, borderRadius: '16px', width: '100%', maxWidth: '440px', padding: isMobile ? '24px 20px' : '32px' }}>
            <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.7rem', color: NAVY, letterSpacing: '0.02em', fontWeight: 700, marginBottom: '12px' }}>
              {t('employer.dashboard.rejectModal.heading')}
            </h2>
            <p style={{ fontSize: '0.9rem', color: '#444', lineHeight: 1.6, marginBottom: '18px' }}>
              {t('employer.dashboard.rejectModal.confirmText', { name: `${rejectModal.worker?.first_name} ${rejectModal.worker?.last_name}` })}
            </p>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#555', marginBottom: '5px' }}>
              {t('employer.dashboard.rejectModal.feedbackLabel')}
            </label>
            <textarea
              value={rejectFeedback}
              onChange={e => setRejectFeedback(e.target.value)}
              placeholder={t('employer.dashboard.rejectModal.feedbackPlaceholder')}
              style={{
                width: '100%', minHeight: '90px', padding: '10px 12px', fontSize: '0.88rem',
                border: '1.5px solid #D8DCE2', borderRadius: '8px', color: NAVY, outline: 'none',
                backgroundColor: WHITE, fontFamily: FONT_BODY, resize: 'vertical', marginBottom: '22px',
              }}
            />
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
              <button
                onClick={() => { setRejectModal(null); setRejectFeedback(''); }}
                style={{ background: 'transparent', border: '1.5px solid #C7CBD1', color: '#555', fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 600, padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', width: isMobile ? '100%' : undefined }}
              >{t('common.cancel')}</button>
              <button
                onClick={handleConfirmReject}
                disabled={rejectingInProgress}
                style={{ background: '#DC2626', border: 'none', color: WHITE, fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 700, padding: '10px 24px', borderRadius: '8px', cursor: rejectingInProgress ? 'default' : 'pointer', opacity: rejectingInProgress ? 0.7 : 1, width: isMobile ? '100%' : undefined }}
              >{rejectingInProgress ? t('employer.dashboard.rejectModal.rejectingButton') : t('employer.dashboard.rejectModal.rejectButton')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
